import LearningAccess from './components/design/LearningAccess';
import { SiteHeader, SiteSidebar } from './components/design/SiteNavigation';
import PageHeading from './components/design/PageHeading';
import './components/design/design-system.css';
import NewHomePage from './components/home/HomePage';
import LearningDemo from './components/home/LearningDemo';
import { retryPendingLearningResults } from './services/learningSessionService';
// src/App.js - 영어 전문 플랫폼 버전
// ===== 핵심 라이브러리 (동기 로드) =====
import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';

// ===== UI 컴포넌트 (동기 로드 - 홈페이지에 필요) =====
import { ConfirmDialog, AlertDialog, LoginRequiredDialog } from './components/DialogUtils';
import { Button } from "./components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card"
import { Input } from "./components/ui/input"
import { Badge } from "./components/ui/badge"
import { Alert, AlertDescription, AlertTitle } from "./components/ui/alert"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./components/ui/tabs"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./components/ui/accordion"
import { ChevronRight, ChevronLeft, Download, Upload, Clock, CreditCard, Menu, X, User, LogOut, Star, Calendar, DollarSign, File, FileText, Settings, Edit, Trash2, Search, BookOpen, PenTool, Target, Sun, Moon, Lock } from 'lucide-react';

// ===== Firebase & 서비스 (동기 로드) =====
import { auth, db, functions, onAuthStateChanged, signIn, signUp, logOut, getUserRole, resetPassword, checkLoginAttempts, recordLoginFailure, resetLoginAttempts, unlockAccount, resendVerificationEmail, verifyEmailWithCode, trackEvent, createSession, validateAndManageSessions, checkCurrentSession, getStoredSessionToken, getActiveSessions, forceLogoutSession, signInWithGoogle, signInWithKakao, handleKakaoCallback, signInWithNaver, getAuthProviders } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, query, orderBy, limit, getDocs, where, doc, getDoc } from 'firebase/firestore';
import useSubscription from './hooks/useSubscription';
import { useToast } from './components/ToastNotification';
import promoCodeService from './services/promoCodeService';
import fileStorageService from './services/FileStorageService';
import { validatePassword, getPasswordStrength, getStrengthColor, getStrengthText } from './utils/passwordValidation';

// ===== 필수 컴포넌트 (동기 로드 - 항상 필요) =====
import Footer from './components/Footer';
import BottomNavigation from './components/BottomNavigation';
import TermsModal from './components/TermsModal';
import PrivacyModal from './components/PrivacyModal';
import OptimizedFileUpload from './components/OptimizedFileUpload';
import MockVariationAnnouncementModal from './components/MockVariationAnnouncementModal';

// ===== 지연 로드 컴포넌트 (Code Splitting) =====
// 관리자 페이지
const AdminPage = lazy(() => import('./components/AdminPage'));
const AdminSetup = lazy(() => import('./components/AdminSetup'));

// 결제 페이지
const PaymentPage = lazy(() => import('./components/PaymentPage'));
const PaymentSuccessPage = lazy(() => import('./components/PaymentSuccessPage'));
const PaymentFailPage = lazy(() => import('./components/PaymentFailPage'));

// 학습 페이지
const BluebookPage = lazy(() => import('./components/BluebookPage'));
const OnlineTestPage = lazy(() => import('./components/OnlineTestPage'));
const OnlineQuiz = lazy(() => import('./components/OnlineQuiz'));
const EnglishLibraryPage = lazy(() => import('./components/EnglishLibraryPage'));

// 대시보드 & 분석
const UserDashboard = lazy(() => import('./components/UserDashboard'));
const AnalyticsPage = lazy(() => import('./components/AnalyticsPage'));
const ReviewPage = lazy(() => import('./components/ReviewPage'));

// 정보 페이지
const FAQPage = lazy(() => import('./components/FAQPage'));
const NoticePage = lazy(() => import('./components/NoticePage'));
const CustomerServicePage = lazy(() => import('./components/CustomerServicePage'));
const PartnershipInquiryPage = lazy(() => import('./components/PartnershipInquiryPage'));
const InquiryPage = lazy(() => import('./components/InquiryPage'));

// 정책 페이지
const TermsOfServicePage = lazy(() => import('./components/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicyPage'));
const RefundPolicyPage = lazy(() => import('./components/RefundPolicyPage'));
const EcommercePolicyPage = lazy(() => import('./components/EcommercePolicyPage'));

// 테스트 컴포넌트
const FirebaseTestComponent = lazy(() => import('./components/FirebaseTestComponent'));

// 모의고사 변형 (비로그인 접근용)
const MockVariationSelectionPage = lazy(() => import('./components/MockVariationSelectionPage'));
const MockVariationTestPage = lazy(() => import('./components/MockVariationTestPage'));

// 이메일 인증/비밀번호 재설정 페이지
const VerifyEmailPage = lazy(() => import('./components/VerifyEmailPage'));
const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage'));

// 토스페이먼츠 SDK (결제 시에만 로드)
const loadTossPayments = () => import('@tosspayments/payment-sdk').then(m => m.loadTossPayments);

// ===== 로딩 스피너 컴포넌트 =====
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">로딩 중...</p>
    </div>
  </div>
);

// 관리자 이메일 화이트리스트
const ADMIN_EMAILS = ['support@bluebookgo.com'];

// 앱 버전 (업데이트 시 이 값과 Firestore의 값을 함께 변경)
const APP_VERSION = '1.0.8';

const App = () => {
  // URL 해시에서 초기 페이지 결정
  const getInitialPage = () => {
    const hash = window.location.hash.substring(1); // # 제거
    const pageName = hash.split('?')[0] === 'download' ? 'library' : hash.split('?')[0]; // ? 이전 부분만 추출
    const validPages = ['home', 'login', 'signup', 'library', 'dashboard', 'subscription', 'pricing', 'faq', 'notice', 'terms', 'privacy', 'refund', 'ecommerce', 'admin', 'test', 'online_test', 'review', 'analytics', 'mock_variation', 'mock_variation_test', 'learning_demo', 'bluebook', 'profile', 'customer_service', 'partnership', 'inquiry', 'mock_variation_free'];
    return validPages.includes(pageName) ? pageName : 'home';
  };

  const [currentPage, setCurrentPageState] = useState(getInitialPage());

  // 페이지 변경 시 URL 업데이트
  const setCurrentPage = (page) => {
    setCurrentPageState(page);
    window.location.hash = page;
  };
  const [onlineNavigationKey, setOnlineNavigationKey] = useState(0);
  const navigateDesignPage = (page) => {
    const target = window.location.pathname + (page === 'profile' ? '?tab=overview' : '') + '#' + page;
    if (window.location.pathname + window.location.search + window.location.hash !== target) window.history.pushState(null, '', target);
    setCurrentPage(page);
  };
  const openDesignLibrary = (category = 'vocabulary') => {
    setLibraryCategory(category === 'bluebook_original' ? 'original' : category);
    navigateDesignPage('library');
  };
  const openDesignLearning = (type) => {
    setResumeVocabularyDay(null); setResumeVocabularyStyle(null); setVocabularyReviewMode(null);
    setResumeGrammarChapter(null); setBluebookStage(null); setBluebookRound(null); setBluebookMode(null); setBluebookSavedSession(null);
    setResumeExamGrade(null); setResumeExamExam(null); setResumeExamQuestionNum(null);
    setOnlineNavigationKey(value => value + 1);
    const page = canAccessMockVariation() && !canAccessOnlineLearning() ? 'mock_variation' : 'online_test';
    const params = new URLSearchParams({tab:'basics'});
    if (typeof type === 'string') params.set('type',type);
    const target = window.location.pathname + '?' + params.toString() + '#' + page;
    if (window.location.pathname + window.location.search + window.location.hash !== target) window.history.pushState(null, '', target);
    setCurrentPage(page);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const [libraryCategory, setLibraryCategory] = useState('vocabulary'); // 자료실 카테고리

  // 온라인 테스트 네비게이션 상태
  const [resumeVocabularyDay, setResumeVocabularyDay] = useState(null);
  const [resumeVocabularyStyle, setResumeVocabularyStyle] = useState(null);
  const [vocabularyReviewMode, setVocabularyReviewMode] = useState(null);
  const [resumeGrammarChapter, setResumeGrammarChapter] = useState(null);
  const [bluebookStage, setBluebookStage] = useState(null);
  const [bluebookRound, setBluebookRound] = useState(null);
  const [bluebookMode, setBluebookMode] = useState(null);
  const [bluebookSavedSession, setBluebookSavedSession] = useState(null);
  const [resumeExamGrade, setResumeExamGrade] = useState(null);
  const [resumeExamExam, setResumeExamExam] = useState(null);
  const [resumeExamQuestionNum, setResumeExamQuestionNum] = useState(null);
  const [freeMockGrade, setFreeMockGrade] = useState(null);  // 무료 모의고사용
  const [freeMockExam, setFreeMockExam] = useState(null);
  const [freeMockQuestionNum, setFreeMockQuestionNum] = useState(null);
  const [reviewPageKey, setReviewPageKey] = useState(0); // 복습 페이지 새로고침용
  const [user, setUser] = useState(null);
  useEffect(() => {
    if (!user?.uid) return;
    const retry = () => retryPendingLearningResults(user.uid);
    retry(); window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [user?.uid]);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState(''); // 로딩 메시지 (카카오 로그인 등)
  const kakaoCallbackProcessingRef = useRef(false); // 카카오 콜백 처리 중 여부
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [pdfs, setPdfs] = useState([]);
  const [message, setMessage] = useState('');                    
  const [downloadingFiles, setDownloadingFiles] = useState(new Set()); 
  const [customerInfo, setCustomerInfo] = useState({name: '', email: '', phone: ''});
  const [paymentMethod, setPaymentMethod] = useState('CARD');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLocalAdmin, setIsLocalAdmin] = useState(false);
  const [userRole, setUserRole] = useState('user'); // 'user' or 'admin'
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showStudyModal, setShowStudyModal] = useState(false); // 학습 시작하기 모달
  const [selectedCardType, setSelectedCardType] = useState(null); // 카드 타입 (basics/bluebook/exam)
  const [activeBadgePopover, setActiveBadgePopover] = useState(null); // 배지 팝오버 상태
  const [isCustomerServiceExpanded, setIsCustomerServiceExpanded] = useState(false); // 고객센터 펼침 상태
  const [isOnlineLearningExpanded, setIsOnlineLearningExpanded] = useState(false); // 온라인 학습 펼침 상태
  const [isDownloadExpanded, setIsDownloadExpanded] = useState(false); // 자료 다운로드 펼침 상태
  const [isReviewExpanded, setIsReviewExpanded] = useState(false); // 복습 펼침 상태
  const [isDashboardExpanded, setIsDashboardExpanded] = useState(false); // 대시보드 펼침 상태
  const [themeMode, setThemeMode] = useState(() => {
    // localStorage에서 테마 설정 불러오기, 기본값은 'auto' (시간대 기반)
    if (typeof window !== 'undefined') {
      return localStorage.getItem('themeMode') || 'auto';
    }
    return 'auto';
  });

  // 현재 다크모드 여부 (themeMode 기반 계산)
  const isDarkMode = (() => {
    if (themeMode === 'dark') return true;
    if (themeMode === 'light') return false;
    if (themeMode === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    // 'auto' - 시간대 기반 (6시-18시 라이트, 그 외 다크)
    const hour = new Date().getHours();
    return hour < 6 || hour >= 18;
  })();
  const [trialStatus, setTrialStatus] = useState(null); // 1일 무료 체험 상태
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false); // 체험 만료 모달
  const [deferredPrompt, setDeferredPrompt] = useState(null); // PWA 설치 프롬프트
  const [showInstallButton, setShowInstallButton] = useState(false); // PWA 설치 버튼 표시
  const [showMockVariationModal, setShowMockVariationModal] = useState(false); // 모의고사 변형문제 안내 팝업
  const { showToast, ToastContainer } = useToast();
  
  const {
    subscription,
    loading: subscriptionLoading,
    canDownload,
    canDownloadCategory,
    downloadPDF,
    refreshSubscription,  // 📚 서버 구독 새로고침
    getStatusMessage,
    getDaysRemaining: getSubscriptionDaysRemaining,  // 별칭으로 변경
    getUsagePercentage,
    isActive,
    isExpired,
    hasSubscription,
    downloadsRemaining,
    totalDownloads,
    planName,
    isUnlimited
  } = useSubscription(user, isLocalAdmin);

  // 섹션별 제한 횟수
  const sectionLimits = subscription?.sectionLimits || null;

  // 온라인 학습 가능 여부 계산 (마스터 플랜만 - 변형패스는 제외)
  const canAccessOnlineLearning = () => {
    // 관리자는 항상 가능
    if (isLocalAdmin) return true;

    // 구독 정보 로딩 중이면 일단 true 반환 (로딩 화면에서 처리)
    if (subscriptionLoading) {
      return true;
    }

    const currentPlanType = subscription?.planType?.toUpperCase?.() || '';

    // 변형패스(MOCK, MOCK_VARIATION)는 온라인 학습 불가 (모의고사 변형만 가능)
    if (currentPlanType === 'MOCK' || currentPlanType === 'MOCK_VARIATION') {
      return false;
    }

    // 마스터 플랜인 경우 (대소문자 무시)
    if (currentPlanType === 'MASTER') {
      return !isExpired;
    }

    // 기존 PREMIUM 구독자도 허용 (하위 호환)
    if (currentPlanType === 'PREMIUM') {
      return !isExpired;
    }

    // 다운로드 횟수가 있으면 허용 (유료 결제자) - 가입보너스 등
    if (hasSubscription && downloadsRemaining > 0 && !isExpired) {
      return true;
    }

    // 무료 체험 중인 경우
    if (trialStatus?.isActive && trialStatus?.onlineTestUnlimited) {
      return true;
    }

    return false;
  };

  // 모의고사 변형 접근 가능 여부 계산
  const canAccessMockVariation = () => {
    // 관리자는 항상 가능
    if (isLocalAdmin) return true;

    // 구독 정보 로딩 중이면 일단 true 반환
    if (subscriptionLoading) {
      return true;
    }

    // MOCK_VARIATION, MOCK, MASTER, PREMIUM(하위호환) 플랜인 경우
    const currentPlanType = subscription?.planType?.toUpperCase?.() || '';
    const validPlans = ['MOCK_VARIATION', 'MOCK', 'MASTER', 'PREMIUM'];

    if (validPlans.includes(currentPlanType)) {
      return !isExpired;
    }

    return false;
  };

  // 플랜 표시 이름 가져오기 (유료 > 체험 > 보너스 우선순위)
  const getDisplayPlanName = () => {
    // 1. 유료 구독 우선 (subscriptions 컬렉션에서)
    if (subscription?.planType) {
      const planType = subscription.planType.toUpperCase();
      if (planType === 'MOCK_VARIATION' || planType === 'MOCK') return '변형패스';
      if (planType === 'ENGLISH_PREMIUM' || planType === 'PREMIUM') return '변형패스';  // 하위호환
      if (planType === 'ENGLISH_BASIC' || planType === 'BASIC') return '베이직';
      if (planType === 'MASTER') return '마스터';
    }

    // 2. 체험 중 (유료 플랜 없고 체험만 있는 경우)
    if (trialStatus?.isActive && !trialStatus?.paidPlan) return '체험';

    // 3. 가입 보너스
    if (subscription?.planType?.toUpperCase() === 'SIGNUP_BONUS') return '만료일';

    return null;
  };

  // D-day 남은 일수 계산 (trial 기준 - 유료 결제 시 trial에 통합됨)
  const getDaysRemaining = () => {
    // 유료 구독이 있고 trial.paidPlan이 있으면 trial.endDate 사용
    if (trialStatus?.paidPlan && trialStatus?.endDate) {
      const endDate = new Date(trialStatus.endDate);
      const now = new Date();
      return Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
    }

    // 체험 중이면 체험 기간 사용
    if (trialStatus?.isActive && trialStatus?.endDate) {
      const endDate = new Date(trialStatus.endDate);
      const now = new Date();
      return Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
    }

    // 구독 만료일 사용 (보너스 등)
    if (subscription?.expiresAt) {
      const expiryDate = new Date(subscription.expiresAt);
      const now = new Date();
      return Math.max(0, Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24)));
    }

    return 0;
  };

  // 섹션 정보 표시 상태
  const [showSectionTooltip, setShowSectionTooltip] = useState(false);

  // 만료 알림 상태 (Plan A: In-app notifications)
  const [showExpiryPopup, setShowExpiryPopup] = useState(false);
  const [showExpiryBanner, setShowExpiryBanner] = useState(false);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [expiryPopupShown, setExpiryPopupShown] = useState(false); // 세션 동안 한 번만 표시
  const [expiryModalShown, setExpiryModalShown] = useState(false); // 3??모달 세션 동안 한 번만 표시

  // 프로모션 코드 상태
  const promoCodeInputRef = useRef(null);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');

  // 추천 코드 상태
  const [referralCode, setReferralCode] = useState('');
  const [appliedReferral, setAppliedReferral] = useState(null);
  const [myReferralCode, setMyReferralCode] = useState(''); // 내 추천 코드
  const [referralStats, setReferralStats] = useState({ count: 0, rewards: 0 }); // 추천 통계
  const [referralLeaderboard, setReferralLeaderboard] = useState(null); // 리더보드

  // 프로모션 코드 정의
  const promoCodes = {
    'EARLYBIRD2024': {
      name: '얼리버드 할인',
      discounts: {
        'BASIC': 4100,      // 19000 -> 14900
        'PREMIUM': 9100,    // 39000 -> 29900
        'UNLIMITED': 19100  // 69000 -> 49900
      },
      validUntil: '2025-01-31',
      description: '2025년 1월 31일까지 적용'
    },
    'STUDENT20': {
      name: '학생 할인',
      discountPercent: 20,
      validUntil: null,
      description: '학생 할인'
    }
  };

  // 자료실로 이동 (카테고리 지정
  const navigateToLibrary = (category = 'vocabulary') => {
    setLibraryCategory(category);
    setCurrentPage('library');
  };

  // 어휘 학습 네비게이션
  const resumeVocabularyTest = (day, style, reviewMode = null) => {
    setResumeVocabularyDay(day);
    setResumeVocabularyStyle(style);
    setVocabularyReviewMode(reviewMode);
    setCurrentPage('online_test');
  };

  // 문법 학습 네비게이션 (복습 포함)
  const resumeGrammarTest = (chapter, reviewMode = null) => {
    console.log('📚 [App] resumeGrammarTest:', { chapter, reviewMode });

    // chapter 신규??(chapter5 -> 5)
    let normalizedChapter = chapter;
    if (typeof chapter === 'string' && chapter.startsWith('chapter')) {
      normalizedChapter = parseInt(chapter.replace('chapter', ''));
    }

    // 문법 챕터 설정
    setResumeGrammarChapter(normalizedChapter);

    // 온라인 테스트 페이지로 이동
    setCurrentPage('online_test');

    // 시간이 지난 후 스크롤(페이지 전환 후
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  // 블루북 학습 네비게이션
  const resumeBluebookTest = (stage, round, mode, savedSession) => {
    console.log('📚 [App] resumeBluebookTest:', { stage, round, mode, savedSession });

    // 블루북 섹션 데이터 설정
    setBluebookStage(stage);
    setBluebookRound(round);
    setBluebookMode(mode);
    setBluebookSavedSession(savedSession);

    // 온라인 테스트 페이지로 이동
    setCurrentPage('online_test');

    // 시간이 지난 후 스크롤(페이지 전환 후
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  // 내신대비 학습 네비게이션
  const resumeExamTest = (grade, exam, questionNum) => {
    console.log('📚 [App] resumeExamTest:', { grade, exam, questionNum });

    // 내신대비 섹션 데이터 설정
    setResumeExamGrade(grade);
    setResumeExamExam(exam);
    setResumeExamQuestionNum(questionNum);

    // 온라인 테스트 페이지로 이동
    setCurrentPage('online_test');

    // 시간이 지난 후 스크롤(페이지 전환 후
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  // 복습 완료 후 복습 페이지로 돌아가기
  const returnToReview = () => {
    setReviewPageKey(prev => prev + 1); // 복습 페이지 새로고침
    setCurrentPage('review');
  };

  // [토스페이먼츠 연동 시 자동 얼리버드 할인 적용 임시 주석처리
  // useEffect(() => {
  //   const today = new Date();
  //   const earlyBirdEndDate = new Date('2025-01-31');
  //
  //   if (today <= earlyBirdEndDate && !appliedPromo) {
  //     setAppliedPromo(promoCodes['EARLYBIRD2024']);
  //     console.log('✅ 얼리버드 할인 자동 적용 (2025-01-31까�?)');
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  // D-Day 헬퍼 함수: 시험일이 지나면 "시행 완료" 표시
  const getDDayInfo = (targetDate) => {
    const diff = Math.ceil((new Date(targetDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return { text: '시행 완료', isCompleted: true };
    if (diff === 0) return { text: 'D-DAY', isCompleted: false };
    return { text: `D-${diff}`, isCompleted: false };
  };

  // 영어 사용 요금제
  const englishPlans = [
    {
      id: 'MOCK_VARIATION',
      name: '모의고사 변형 패스',
      price: 10000,
      originalPrice: 10000,
      downloads: 100,
      duration: 365, // 1년
      popular: true,
      features: ['모의고사 변형문제 전체 이용', '1년 이용권', '고1~고3 전 학년', '신규 모의고사 자동 추가']
    },
    {
      id: 'MASTER',
      name: '올인원 마스터',
      price: 30000,
      originalPrice: 30000,
      downloads: 500,
      duration: 365, // 1년
      popular: false,
      features: ['모든 콘텐츠 이용', '1년 이용권', '어휘 + 문법 + 듣기 + 블루북', '모의고사 변형문제 포함']
    }
  ];

  // 프로모션 코드 적용 함수 (Firestore 기반)
  const applyPromoCode = async (code) => {
    if (!code || code.trim() === '') {
      setPromoError('프로모션 코드를 입력해주세요');
      setAppliedPromo(null);
      return false;
    }

    try {
      const validation = await promoCodeService.validatePromoCode(code);

      if (!validation.valid) {
        setPromoError(validation.message);
        setAppliedPromo(null);
        return false;
      }

      const promoData = validation.promoCode;

      // 🎁 플랜 부여 코드 처리 (type: 'plan_grant')
      if (promoData.type === 'plan_grant') {
        // 로그인 필요
        if (!user) {
          setPromoError('플랜 부여 코드를 사용하려면 먼저 로그인해주세요.');
          return false;
        }

        // 플랜 부여 적용
        const result = await promoCodeService.applyPlanGrantCode(code, user.uid, user.email);

        if (result.success) {
          setPromoError('');
          if (promoCodeInputRef.current) promoCodeInputRef.current.value = '';
          // 구독 정보 새로고침
          if (refreshSubscription) {
            await refreshSubscription();
          }
          // 성공 메시지 표시
          alert(`🎉 ${result.message}\n\n다운로드: ${result.downloadsRemaining}회\n만료일: ${new Date(result.expiresAt).toLocaleDateString('ko-KR')}`);
          return true;
        } else {
          setPromoError(result.message);
          return false;
        }
      }

      // 일반 할인 프로모션 코드 처리
      const formattedPromo = {
        code: promoData.code,
        name: promoData.description || promoData.code,
        type: promoData.type,
        discount: promoData.discount,
        planDiscounts: promoData.planDiscounts,
        validUntil: promoData.validUntil ? promoData.validUntil.toDate() : null
      };

      setAppliedPromo(formattedPromo);
      setPromoError('');
      if (promoCodeInputRef.current) promoCodeInputRef.current.value = '';
      return true;
    } catch (error) {
      console.error('❌ 프로모션 코드 적용 실패:', error);
      setPromoError('프로모션 코드 사용 중 오류가 발생했습니다');
      setAppliedPromo(null);
      return false;
    }
  };

  // 할인된 가격 계산 (프로모션 코드 + 추천 코드 할인 조합)
  const getDiscountedPrice = (plan) => {
    let finalPrice = plan.price;

    // 1. 프로모션 코드 할인 적용
    if (appliedPromo) {
      // 플랜에 고정 할인 금액이 있는 경우
      if (appliedPromo.planDiscounts && appliedPromo.planDiscounts[plan.id]) {
        finalPrice = plan.price - appliedPromo.planDiscounts[plan.id];
      }
      // 타입에 따른 할인 계산
      else if (appliedPromo.type === 'percentage') {
        finalPrice = Math.floor(plan.price * (1 - appliedPromo.discount / 100));
      } else if (appliedPromo.type === 'fixed') {
        finalPrice = Math.max(0, plan.price - appliedPromo.discount);
      }
    }

    // 2. 추천 코드 할인 (2회 이하만, 피추천인에게 15% 할인)
    if (appliedReferral) {
      finalPrice = Math.floor(finalPrice * 0.85); // 15% 할인
    }

    return finalPrice;
  };

  // 추천 코드 생성 (사용자 UID 기반)
  const generateReferralCode = (userId) => {
    if (!userId) return '';
    // ??앞 6자리 + 뒤 4자리 = 10자리 추천 코드
    const code = userId.substring(0, 6).toUpperCase() + userId.substring(userId.length - 4).toUpperCase();
    return `BLUEBOOK${code}`;
  };

  // 추천 링크 생성
  const generateReferralLink = (referralCode) => {
    const baseUrl = 'https://bluebookgo.com';
    return `${baseUrl}/?ref=${referralCode}`;
  };

  // 시즌 이벤트 확인 (2026년 2월까지)
  const isSeasonEvent = () => {
    const now = new Date();
    const eventEnd = new Date('2026-02-28T23:59:59+09:00');
    return now <= eventEnd;
  };

  // 공유 텍스트 생성
  const generateShareText = (referralCode) => {
    const link = generateReferralLink(referralCode);
    return `📚 Bluebook Go 추천해요!
수능영어 올인원 플랫폼! 단어·듣기·문법·모의고사가 한 곳에서

👉 ${link}

📌 추천 코드: ${referralCode}`;
  };

  // 공유 텍스트 복사
  const copyShareText = (referralCode) => {
    const text = generateShareText(referralCode);
    navigator.clipboard.writeText(text);
    showToast('✅ 공유 메시지가 복사되었습니다!\n카카오톡이나 문자에 붙여넣기 해보세요.', 'success');
  };

  // 링크만 복사
  const copyShareLink = (referralCode) => {
    const link = generateReferralLink(referralCode);
    navigator.clipboard.writeText(link);
    showToast('✅ 추천 링크가 복사되었습니다!', 'success');
  };

  // 카카오톡 공유
  const shareToKakao = (referralCode) => {
    const link = generateReferralLink(referralCode);

    // Kakao SDK 확인
    if (!window.Kakao) {
      showToast('카카오 SDK를 불러오는 중입니다. 잠시 후 다시 시도해주세요.', 'warning');
      return;
    }

    // Kakao SDK 초기화 확인
    if (!window.Kakao.isInitialized()) {
      const kakaoKey = process.env.REACT_APP_KAKAO_JS_KEY;
      if (kakaoKey) {
        window.Kakao.init(kakaoKey);
      } else {
        // SDK가 없으면 메시지 복사로 대체
        copyShareText(referralCode);
        showToast('카카오톡 공유 대신 메시지가 복사되었습니다!', 'info');
        return;
      }
    }

    try {
      window.Kakao.Share.sendDefault({
        objectType: 'feed',
        content: {
          title: '📚 Bluebook Go 추천해요!',
          description: '가입하면 프리미엄 3일 혜택!',
          imageUrl: 'https://bluebookgo.com/logo512.png',
          link: {
            mobileWebUrl: link,
            webUrl: link,
          },
        },
        buttons: [
          {
            title: '🎁 혜택받고 시작하기',
            link: {
              mobileWebUrl: link,
              webUrl: link,
            },
          },
        ],
      });
    } catch (error) {
      console.error('카카오 공유 실패:', error);
      // 실패 시 메시지 복사로 대체
      copyShareText(referralCode);
      showToast('카카오톡 공유 대신 메시지가 복사되었습니다!', 'info');
    }
  };

  // 추천 코드 사용량
  const applyReferralCode = async (code) => {
    if (!code || code.length < 10) {
      showToast('유효하지 않은 추천 코드입니다', 'error');
      return false;
    }

    try {
      // 로그인되어 있으면 바로 백엔드에서 보상 처리
      if (user) {
        console.log('📚 로그인 사용자 - 백엔드에서 추천 보상 처리');

        const applyReferralRewardFunc = httpsCallable(functions, 'applyReferralReward');
        const result = await applyReferralRewardFunc({ referralCode: code.toUpperCase() });

        if (result.data.success) {
          setAppliedReferral({
            code: code.toUpperCase(),
            phase: result.data.phase,
            referrerBonus: result.data.referrerBonus,
            referredBonus: result.data.referredBonus,
            renewalCoupon: result.data.renewalCoupon
          });

          showToast(`??${result.data.message}`, 'success');

          // 페이지 새로고침하여 구독 정보 업데이트
          setTimeout(() => {
            window.location.reload();
          }, 2000);

          return true;
        }
      } else {
        // 비로그인 사용자는 온라인스토리지에서만 검증(나중에 로그인/회원가입 시 적용)
        console.log('📚 비로그인 사용자 - 로컬스토리지에서 검증만 수행');

        const referralsRef = db.collection('users');
        const snapshot = await referralsRef.get();

        let referrerFound = false;
        let referrerId = null;
        let referrerName = '';

        snapshot.forEach((doc) => {
          const referralCode = generateReferralCode(doc.id);
          if (referralCode === code.toUpperCase()) {
            referrerFound = true;
            referrerId = doc.id;
            referrerName = doc.data().displayName || '친구';
          }
        });

        if (referrerFound) {
          setAppliedReferral({
            code: code.toUpperCase(),
            referrerId: referrerId,
            referrerName: referrerName,
            pendingReward: true // 로그인 시 보상 적용 필요
          });

          showToast('✅ 친구 추천 코드가 적용되었습니다! 로그인 시 보상이 지급됩니다.', 'success');
          return true;
        } else {
          showToast('존재하지 않는 추천 코드입니다', 'error');
          return false;
        }
      }
    } catch (error) {
      console.error('추천 코드 적용 오류:', error);

      if (error.message.includes('already-exists')) {
        showToast('이미 추천 보상을 받았습니다', 'error');
      } else if (error.message.includes('본인의 추천 코드')) {
        showToast('본인의 추천 코드는 사용할 수 없습니다', 'error');
      } else {
        showToast('추천 코드 확인 중 오류가 발생했습니다', 'error');
      }

      return false;
    }
  };

  // 🌙 다크 모드 효과 적용
  useEffect(() => {
    // themeMode 저장
    localStorage.setItem('themeMode', themeMode);

    // 실제 다크모드 적용
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // 시스템 설정 변경 감지 (system 모드일 때만)
    if (themeMode === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        if (mediaQuery.matches) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      };
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    // auto 모드일 때 1분마다 시간 체크 (시간대 변경 감지)
    if (themeMode === 'auto') {
      const interval = setInterval(() => {
        const hour = new Date().getHours();
        const shouldBeDark = hour < 6 || hour >= 18;
        if (shouldBeDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      }, 60000); // 1분마다 체크
      return () => clearInterval(interval);
    }
  }, [themeMode, isDarkMode]);

  // 모의고사 변형문제 팝업 - 오늘 하루 그만 보기 핸들러
  const handleMockVariationDontShowToday = () => {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem('mockVariationModalDismissDate', today);
    setShowMockVariationModal(false);
  };

  // 🔄 앱 버전 체크 - 구버전 캐시 자동 새로고침
  useEffect(() => {
    const checkAppVersion = async () => {
      try {
        const versionDocRef = doc(db, 'systemConfig', 'appVersion');
        const versionDoc = await getDoc(versionDocRef);

        if (versionDoc.exists()) {
          const serverVersion = versionDoc.data().version;
          const storedVersion = localStorage.getItem('app_version');

          console.log(`📱 앱 버전 체크: 클라이언트=${APP_VERSION}, 서버=${serverVersion}, 저장=${storedVersion}`);

          // 서버 버전과 다르면 캐시 삭제 후 새로고침
          if (APP_VERSION !== serverVersion && storedVersion !== serverVersion) {
            console.log('🔄 새 버전 감지! 캐시 삭제 후 새로고침...');

            // Service Worker 캐시 삭제
            if ('caches' in window) {
              const cacheNames = await caches.keys();
              await Promise.all(cacheNames.map(name => caches.delete(name)));
              console.log('✅ 캐시 삭제 완료');
            }

            // 버전 저장 후 새로고침 (무한 루프 방지)
            localStorage.setItem('app_version', serverVersion);
            window.location.reload(true);
            return;
          }

          // 버전이 같으면 로컬 저장
          if (storedVersion !== APP_VERSION) {
            localStorage.setItem('app_version', APP_VERSION);
          }
        }
      } catch (error) {
        console.warn('⚠️ 버전 체크 실패 (무시됨):', error.message);
      }
    };

    checkAppVersion();
  }, []);

  // Kakao SDK 초기화
  useEffect(() => {
    if (window.Kakao && !window.Kakao.isInitialized()) {
      const kakaoKey = process.env.REACT_APP_KAKAO_JS_KEY;
      if (kakaoKey && kakaoKey !== 'your_javascript_key_here') {
        try {
          window.Kakao.init(kakaoKey);
          console.log('✅ Kakao SDK 초기화 완료');
        } catch (error) {
          console.warn('⚠️ Kakao SDK 초기화 실패:', error);
        }
      } else {
        console.log('ℹ️ Kakao SDK 키가 설정되지 않았습니다 (카카오톡 공유 기능 비활성화)');
      }
    }
  }, []);

  // Kakao 로그인 콜백 처리 (리다이렉트 방식)
  useEffect(() => {
    const processKakaoCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');

      // Kakao 로그인 콜백인 경우에만 처리
      if (code && state === 'kakao_login') {
        console.log('🔐 Kakao 로그인 콜백 감지, 처리 시작...');
        kakaoCallbackProcessingRef.current = true; // 콜백 처리 중 플래그 설정
        setLoading(true);
        setLoadingMessage('카카오 로그인 처리 중...');

        try {
          const result = await handleKakaoCallback();

          if (result && result.success) {
            console.log('✅ Kakao 로그인 성공:', result.user);
            setLoadingMessage('로그인 완료! 잠시만 기다려주세요...');
            // onAuthStateChanged가 사용자 상태를 업데이트할 때까지 잠시 대기
            // alert은 제거하고 토스트로 대체
            setTimeout(() => {
              if (result.isNewUser) {
                showToast('🎉 카카오 계정으로 회원가입이 완료되었습니다! 3일 무료 체험이 시작됩니다.', 'success', 5000);
              } else {
                showToast('✅ 카카오 로그인 성공!', 'success', 3000);
              }
            }, 500);
          } else if (result && !result.success) {
            console.error('❌ Kakao 로그인 실패:', result.error);
            showToast('카카오 로그인에 실패했습니다: ' + (result.error || '알 수 없는 오류'), 'error', 5000);
            kakaoCallbackProcessingRef.current = false;
            setLoading(false);
            setLoadingMessage('');
          }
        } catch (error) {
          console.error('❌ Kakao 콜백 처리 오류:', error);
          showToast('카카오 로그인 처리 중 오류가 발생했습니다.', 'error', 5000);
          kakaoCallbackProcessingRef.current = false;
          setLoading(false);
          setLoadingMessage('');
        }
        // 성공 시 loading은 onAuthStateChanged에서 해제됨
      }
    };

    processKakaoCallback();
  }, [showToast]);

  // Naver 로그인 콜백 처리
  const naverCallbackProcessingRef = useRef(false);
  useEffect(() => {
    const processNaverCallback = async () => {
      // /auth/naver/callback 경로 체크
      if (!window.location.pathname.includes('/auth/naver/callback')) {
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');

      // 에러 처리
      if (error) {
        console.error('❌ Naver 로그인 오류:', error);
        showToast('네이버 로그인이 취소되었습니다.', 'error');
        window.history.replaceState({}, document.title, '/#home');
        setCurrentPage('home');
        return;
      }

      // code가 없으면 리턴
      if (!code) {
        return;
      }

      // 이미 처리 중이면 리턴
      if (naverCallbackProcessingRef.current) {
        return;
      }

      // state 검증
      const savedState = sessionStorage.getItem('naver_state');
      if (state !== savedState) {
        console.error('❌ Naver state 불일치');
        showToast('보안 검증에 실패했습니다. 다시 시도해주세요.', 'error');
        window.history.replaceState({}, document.title, '/#home');
        setCurrentPage('home');
        return;
      }

      console.log('🔐 Naver 로그인 콜백 감지, 처리 시작...');
      naverCallbackProcessingRef.current = true;
      setLoading(true);
      setLoadingMessage('네이버 로그인 처리 중...');

      try {
        // Cloud Function 호출하여 로그인 처리
        const result = await signInWithNaver(code);

        if (result && result.success) {
          console.log('✅ Naver 로그인 성공:', result.user);
          setLoadingMessage('로그인 완료! 잠시만 기다려주세요...');

          // 세션 생성
          if (result.user && result.user.uid) {
            await createSession(result.user.uid);
            await validateAndManageSessions(result.user.uid, getStoredSessionToken());
          }

          // URL 정리
          sessionStorage.removeItem('naver_state');
          sessionStorage.removeItem('naver_redirect');
          window.history.replaceState({}, document.title, '/#home');

          setTimeout(() => {
            if (result.isNewUser) {
              showToast('🎉 네이버 계정으로 회원가입이 완료되었습니다! 3일 무료 체험이 시작됩니다.', 'success', 5000);
            } else {
              showToast('✅ 네이버 로그인 성공!', 'success', 3000);
            }
            setCurrentPage('home');
          }, 500);
        } else {
          console.error('❌ Naver 로그인 실패:', result?.error);
          showToast('네이버 로그인에 실패했습니다: ' + (result?.error || '알 수 없는 오류'), 'error', 5000);
          window.history.replaceState({}, document.title, '/#home');
          setCurrentPage('home');
        }
      } catch (error) {
        console.error('❌ Naver 콜백 처리 오류:', error);
        showToast('네이버 로그인 처리 중 오류가 발생했습니다.', 'error', 5000);
        window.history.replaceState({}, document.title, '/#home');
        setCurrentPage('home');
      } finally {
        naverCallbackProcessingRef.current = false;
        setLoading(false);
        setLoadingMessage('');
      }
    };

    processNaverCallback();
  }, [showToast]);

  // PWA 설치 프롬프트 이벤트 리스너
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // 브라우저 기본 설치 배너 방지
      e.preventDefault();
      // 이벤트 저장
      setDeferredPrompt(e);
      console.log('PWA 설치 프롬프트 준비됨');
    };

    const handleAppInstalled = () => {
      // 설치 완료 후 버튼 숨김
      setShowInstallButton(false);
      setDeferredPrompt(null);
      console.log('PWA 설치 완료');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // 앱 설치된 경우(standalone 모드) 버튼 숨김, 아니면 모바일/태블릿에서 표시
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                         window.navigator.standalone === true; // iOS Safari

    if (isStandalone) {
      setShowInstallButton(false);
      console.log('이미 PWA로 실행 중');
    } else {
      // 모든 디바이스에서 설치 버튼 표시 (PC, 모바일, 태블릿)
      setShowInstallButton(true);
      console.log('PWA 설치 버튼 표시');
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // iOS Safari 감�?
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
           (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
  };

  // PWA 설치 실행
  const handleInstallClick = async () => {
    // iOS Safari인 경우 안내 메시지
    if (isIOS()) {
      showToast('Safari에서 공유 버튼(□↑) → "홈 화면에 추가"를 선택하세요', 'info', 5000);
      return;
    }

    // Android/Chrome에서 beforeinstallprompt가 준비된 경우
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('✅ 사용자가 PWA 설치 수락');
        showToast('✅ 설치 중...', 'success');
      } else {
        console.log('❌ 사용자가 PWA 설치 거절');
      }

      setDeferredPrompt(null);
    } else {
      // beforeinstallprompt가 아직 없는 경우 (Chrome 정책)
      showToast('브라우저 메뉴(⋮) → "앱 설치" 또는 "홈 화면에 추가"를 선택하세요', 'info', 5000);
    }
  };

  // Firebase Auth State 리스너
  useEffect(() => {
    console.log('📚 Firebase Auth State 리스너 시작...');

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Firebase 사용자가 로그인된 상태
        const mockUser = {
          uid: firebaseUser.uid,
          email: firebaseUser.email || 'support@bluebookgo.com',
          displayName: firebaseUser.displayName || '테스트 사용자',
          isAnonymous: firebaseUser.isAnonymous,
          firebaseUser: firebaseUser
        };

        // ===== 세션 관리 (동시 2대 제한) =====
        try {
          const existingToken = getStoredSessionToken();

          if (!existingToken) {
            // 새 세션 생성
            console.log('🔐 새 세션 생성 중...');
            await createSession(firebaseUser.uid);
          }

          // 세션 검증 및 관리 (2대 초과 시 오래된 세션 비활성화)
          const currentToken = getStoredSessionToken();
          const validation = await validateAndManageSessions(firebaseUser.uid, currentToken);

          if (validation.kickedSession) {
            console.log('🔒 다른 기기 세션이 종료되었습니다');
          }
        } catch (sessionError) {
          console.warn('⚠️ 세션 관리 중 오류 (무시):', sessionError);
        }
        // ===== 세션 관리 끝 =====

        setUser(mockUser);
        console.log('✅ Firebase Auth 사용자 로그인 완료:', mockUser);

        // 추천 코드 생성
        const myCode = generateReferralCode(firebaseUser.uid);
        setMyReferralCode(myCode);
        console.log('🏠 홈추천 코드:', myCode);

        // Firestore에서 사용자 역할 및 체험 상태 가져오기
        const loadUserRoleAndTrial = async () => {
          try {
            console.log('📚 사용자 역할 및 체험 상태 조회 시작');
            console.log('   - UID:', firebaseUser.uid);
            console.log('   - 이메일', firebaseUser.email);

            const role = await getUserRole(firebaseUser.uid);
            console.log('✅ 역할 조회 성공:', role);

            // 관리자 이메일인데 role이 user이면 경고
            if (firebaseUser.email === 'support@bluebookgo.com' && role !== 'admin') {
              console.warn('⚠️ 관리자 이메일이지만 role이 admin이 아닙니다!');
              console.warn('   현재 role:', role);
              console.warn('   Firestore users 문서를 확인하세요.');
            }

            setUserRole(role);
            const isAdmin = role === 'admin';
            setIsLocalAdmin(isAdmin);
            console.log('📚 최종 상태 설정 완료');
            console.log('   - userRole:', role);
            console.log('   - isLocalAdmin:', isAdmin);

            if (isAdmin) {
              console.log('✅ 관리자 권한 활성화됨 - 관리자 메뉴가 표시됩니다');
            }

            // 1일 무료 체험 상태 확인
            const { doc: firestoreDoc, getDoc: firestoreGetDoc } = await import('firebase/firestore');
            const userDocRef = firestoreDoc(db, 'users', firebaseUser.uid);
            const userDoc = await firestoreGetDoc(userDocRef);

            if (userDoc.exists()) {
              const userData = userDoc.data();
              const trial = userData.trial;
              const userSubscription = userData.subscription;

              // 유료 구독이 있으면 체험 상태 무시
              const hasActiveSubscription = userSubscription &&
                userSubscription.status === 'active' &&
                userSubscription.planType &&
                userSubscription.planType !== 'signup_bonus';

              if (trial && !hasActiveSubscription) {
                const now = new Date();
                const trialEndDate = new Date(trial.endDate);
                const hoursRemaining = Math.max(0, (trialEndDate - now) / (1000 * 60 * 60));
                const isTrialActive = trial.isActive && now < trialEndDate;

                const trialInfo = {
                  isActive: isTrialActive,
                  hoursRemaining: Math.round(hoursRemaining * 10) / 10,
                  endDate: trial.endDate,
                  pdfDownloads: trial.pdfDownloads || 3,
                  pdfDownloadsUsed: trial.pdfDownloadsUsed || 0,
                  onlineTestUnlimited: trial.onlineTestUnlimited && isTrialActive
                };

                setTrialStatus(trialInfo);
                console.log('📚 체험 상태:', trialInfo);

                // 체험 만료 시 모달 표시
                if (!isTrialActive && trial.isActive) {
                  console.log('⏰ 체험 기간 만료');
                  setShowTrialExpiredModal(true);
                }

                // 체험 1시간 전 알림
                if (isTrialActive && hoursRemaining <= 1 && hoursRemaining > 0) {
                  showToast('무료 체험이 ' + Math.round(hoursRemaining * 60) + '분 후 종료됩니다. 구독하시면 계속 사용 가능합니다!', 'warning', 10000);
                }
              } else if (hasActiveSubscription) {
                // 유료 구독자이면 체험 상태 초기화
                setTrialStatus(null);
                console.log('📚 유료 구독 감지 - 체험 상태 무시');
              }
            }
          } catch (error) {
            console.error('❌ 사용자 역할/체험 로드 실패');
            console.error('   - 에러 메시지:', error.message);
            console.error('   - 에러 코드:', error.code);
            console.error('   - 전체 에러:', error);
            setUserRole('user');
            setIsLocalAdmin(false);
          }
        };

        // 사용자 역할 로드 + 섹션 확인 + 추천 통계 + 리더보드를 병렬로 실행 (초기 로딩 속도 개선)
        const checkAndAddNewSectionsFunc = httpsCallable(functions, 'checkAndAddNewSections');
        const getMyReferralStatsFunc = httpsCallable(functions, 'getMyReferralStats');

        Promise.all([
          loadUserRoleAndTrial(),
          checkAndAddNewSectionsFunc().catch(err => {
            console.error('⚠️ 새 섹션 확인 실패:', err);
            return { data: { success: false } };
          }),
          getMyReferralStatsFunc().catch(err => {
            console.error('⚠️ 추천 통계 조회 실패:', err);
            return { data: { success: false, count: 0, rewards: 0 } };
          }),
          Promise.resolve({ data: { success: false, leaderboard: [] } })
        ]).then(([_, sectionResult, statsResult, leaderboardResult]) => {
          if (sectionResult?.data?.success && sectionResult.data.newSections?.length > 0) {
            console.log('🏠 홈 섹션 추가됨:', sectionResult.data.newSections);
            const sectionNames = sectionResult.data.newSections.map(s => s.name).join(', ');
            showToast(sectionNames + ' 섹션이 추가되었습니다! 각 ' + sectionResult.data.newSections[0].freeDownloads + '개씩 무료 체험 가능', 'success', 5000);
          }

          // 추천 통계 설정
          if (statsResult?.data?.success) {
            setReferralStats(statsResult.data);
            console.log('📊 추천 통계 로드 완료:', statsResult.data);
          }

          // 리더보드 설정
          if (leaderboardResult?.data?.success) {
            setReferralLeaderboard(leaderboardResult.data);
            console.log('🏆 리더보드 로드 완료:', leaderboardResult.data);
          }
        });

        // 로그인??📚?중인 추천 코드 보상 사용량
        if (appliedReferral && appliedReferral.pendingReward) {
          console.log('📚 로그인??📚?중이??추천 보상 사용량:', appliedReferral.code);
          try {
            const applyReferralRewardFunc = httpsCallable(functions, 'applyReferralReward');
            const result = await applyReferralRewardFunc({ referralCode: appliedReferral.code });

            if (result.data.success) {
              setAppliedReferral({
                code: appliedReferral.code,
                phase: result.data.phase,
                referrerBonus: result.data.referrerBonus,
                referredBonus: result.data.referredBonus,
                renewalCoupon: result.data.renewalCoupon,
                pendingReward: false
              });

              showToast(result.data.message, 'success');
            }
          } catch (error) {
            console.error('❌ 추천 보상 적용 실패:', error);
            if (!error.message.includes('already-exists')) {
              showToast('추천 보상 적용 중 오류가 발생했습니다', 'error');
            }
          }
        }
      } else {
        // 로그아웃 상태
        // 카카오 콜백 처리 중이면 로딩 상태 유지 (경쟁 상태 방지)
        if (kakaoCallbackProcessingRef.current) {
          console.log('📚 카카오 콜백 처리 중 - 로그아웃 상태 무시');
          return; // 카카오 콜백 처리가 끝날 때까지 대기
        }
        setUser(null);
        setUserRole('user');
        setIsLocalAdmin(false);
        console.log('📚 Firebase Auth 로그아웃 상태');
      }

      // 카카오 콜백 처리 완료 후 플래그 해제
      if (kakaoCallbackProcessingRef.current && firebaseUser) {
        kakaoCallbackProcessingRef.current = false;
        setLoadingMessage('');
      }

      setLoading(false);
    });

    // Cleanup
    return () => unsubscribe();
  }, []);

  // 주기적 세션 검증 (3분마다) - 다른 기기에서 로그인 시 감지
  useEffect(() => {
    if (!user) return;

    const checkSession = async () => {
      try {
        const sessionToken = getStoredSessionToken();
        if (!sessionToken) return;

        const result = await checkCurrentSession(user.uid, sessionToken);

        if (!result.valid) {
          if (result.reason === 'session_deactivated') {
            showToast('다른 기기에서 로그인하여 이 세션이 종료됩니다.', 'warning', 5000);
            setTimeout(async () => {
              await logOut();
            }, 3000);
          }
        }
      } catch (error) {
        console.warn('⚠️ 세션 검증 중 오류:', error);
      }
    };

    // 3분마다 세션 검증
    const interval = setInterval(checkSession, 3 * 60 * 1000);

    // 초기 검증
    checkSession();

    return () => clearInterval(interval);
  }, [user]);

  // URL 기반 페이지 라우팅
  useEffect(() => {
    const handleUrlRouting = () => {
      const path = window.location.pathname;
      const search = window.location.search;
      const hash = window.location.hash.replace('#', ''); // #admin-setup -> admin-setup

      // URL 파라미터에서 추천 코드 확인
      const urlParams = new URLSearchParams(search);
      const refCode = urlParams.get('ref');
      if (refCode && refCode.trim() !== '') {
        console.log('📚 URL에서 추천 코드 발견:', refCode);
        setReferralCode(refCode.toUpperCase());
        // 가격 페이지로 이동하여 추천 코드가 표시되도록
        if (currentPage !== 'pricing') {
          setCurrentPageState('pricing');
        }
        // URL에서 ref 파라미터 제거 (깔끔한 URL 위해)
        const newUrl = window.location.pathname + window.location.hash;
        window.history.replaceState({}, '', newUrl);

        // 환영 메시지 표시
        setTimeout(() => {
          showToast('추천 코드 ' + refCode + '가 자동으로 입력되었습니다. 가입시 혜택을 받으세요!', 'success');
        }, 500);
      }

      // Hash 기반 라우팅 (우선순위 높음)
      if (hash) {
        console.log('📚 Hash 라우팅:', hash);
        setCurrentPageState(hash);
        return;
      }

      // Path 기반 처리
      if (path === '/payment/success') {
        setCurrentPageState('payment-success');
      } else if (path === '/payment/fail') {
        setCurrentPageState('payment-fail');
      }

      // Query 기반 처리
      if (search.includes('orderId') && search.includes('paymentKey')) {
        setCurrentPageState('payment-success');
      } else if (search.includes('code') && search.includes('message')) {
        setCurrentPageState('payment-fail');
      }
    };

    handleUrlRouting();
    window.addEventListener('popstate', handleUrlRouting);
    window.addEventListener('hashchange', handleUrlRouting); // Hash 변�?감�?

    return () => {
      window.removeEventListener('popstate', handleUrlRouting);
      window.removeEventListener('hashchange', handleUrlRouting);
    };
  }, []);

  // 📚 페이지 변경 시 스크롤을 최상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  // 🔍 SEO: 페이지별 동적 Title 및 Meta Description 설정
  // 네이버 권장: 제목 40자 이내, 설명 80자 이내
  useEffect(() => {
    const seoConfig = {
      home: {
        title: '수능영어 모의고사 변형문제 | 블루북고',
        description: '수능영단어, 수능듣기, 모의고사 변형문제를 한 곳에서. 고1·고2·고3 완벽 대비, AI 해설 제공.'
      },
      mock_variation: {
        title: '모의고사 변형문제 | 블루북고',
        description: '고1, 고2, 고3 모의고사 변형문제. 수능영어 온라인 학습, AI 해설 제공. 10,000원/1년.'
      },
      mock_variation_test: {
        title: '모의고사 변형문제 풀기 | 블루북고',
        description: '수능영어 모의고사 변형문제를 풀어보세요. 상세한 AI 해설 제공.'
      },
      online_test: {
        title: '온라인 영어 학습 | 블루북고',
        description: '수능영단어, 영어듣기, 영문법 체계적 학습. 44일 어휘, 18챕터 문법 완성.'
      },
      library: {
        title: '영어 학습 자료실 | 블루북고',
        description: '모의고사 변형문제, 영단어, 문법 자료 PDF 다운로드.'
      },
      pricing: {
        title: '구독 요금제 | 블루북고',
        description: '프리미엄 구독으로 모든 학습 콘텐츠 무제한 이용. 합리적인 가격.'
      },
      signup: {
        title: '회원가입 | 블루북고',
        description: '3초 만에 가입! 무료 학습 혜택과 소셜 로그인 지원.'
      },
      login: {
        title: '로그인 | 블루북고',
        description: '블루북고에 로그인하여 학습을 계속하세요.'
      },
      faq: {
        title: '자주 묻는 질문 | 블루북고',
        description: '블루북고 이용 관련 FAQ를 확인하세요.'
      },
      dashboard: {
        title: '내 학습 대시보드 | 블루북고',
        description: '나의 학습 현황과 진도를 확인하세요.'
      }
    };

    const defaultSeo = seoConfig.home;
    const pageSeo = seoConfig[currentPage] || defaultSeo;

    // Title 업데이트
    document.title = pageSeo.title;

    // Meta Description 업데이트
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', pageSeo.description);
    }

    // Open Graph 태그 업데이트
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogTitle) ogTitle.setAttribute('content', pageSeo.title);
    if (ogDescription) ogDescription.setAttribute('content', pageSeo.description);

  }, [currentPage]);

  // ⚙️ 관리자 상태는 실제 Firestore role 기반으로 관리됩니다
  // Firebase Auth State 리스너에서 자동으로 처리됨
  // 📚 Analytics: 페이지 뷰 추적
  useEffect(() => {
    trackEvent('page_view', {
      page_title: currentPage,
      page_location: window.location.href,
      user_id: user?.uid || 'anonymous'
    });
  }, [currentPage, user]);

  // Toast 이벤트 리스너
  useEffect(() => {
    const handleToast = (event) => {
      const { message, type, duration } = event.detail;
      showToast(message, type, duration);
    };

    window.addEventListener('showToast', handleToast);
    
    return () => {
      window.removeEventListener('showToast', handleToast);
    };
  }, [showToast]);

  // 만료일 체크 및 알림 표시 (Plan A: In-app notifications)
  useEffect(() => {
    if (!user || !subscription || !subscription.isBonus || !subscription.expiresAt) {
      return;
    }

    const daysRemaining = subscription.daysRemaining || 0;

    // 만료된 경우: 가격 페이지로 자동 리다이렉트
    if (daysRemaining <= 0 && subscription.status !== 'EXPIRED') {
      console.log('무료 체험 만료 - 가격 페이지로 리다이렉트');
      setCurrentPage('pricing');
      showToast('무료 체험이 만료되었습니다. 구독 플랜을 선택해주세요.', 'error', 8000);
      return;
    }

    // 3일 이하: 세션 동안 한 번만 모달 팝업
    if (daysRemaining <= 3 && daysRemaining > 0 && !expiryModalShown) {
      setShowExpiryModal(true);
      setShowExpiryBanner(true);
      setExpiryModalShown(true);
      return;
    }

    // 5일 이하: 상단 배너 표시
    if (daysRemaining <= 5 && daysRemaining > 3) {
      setShowExpiryBanner(true);
      return;
    }

    // 7일 이하: 로그인 시 한 번만 팝업
    if (daysRemaining <= 7 && daysRemaining > 5 && !expiryPopupShown) {
      setShowExpiryPopup(true);
      setExpiryPopupShown(true);
      return;
    }

  }, [user, subscription, expiryPopupShown, expiryModalShown, showToast]);

  // BroadcastChannel로 탭 간 통신 (이메일 인증 후
  useEffect(() => {
    const channel = new BroadcastChannel('email_verification');

    // 다른 탭의 이메일 인증 완료 메시지 수신
    channel.onmessage = (event) => {
      if (event.data.type === 'EMAIL_VERIFIED') {
        console.log('다른 탭에서 이메일 인증 완료');

        // 현재 탭도 자동으로 이동하고 성공 메시지 표시
        setCurrentPage('home');
        showToast('이메일 인증이 완료되었습니다. 로그인되었습니다.', 'success', 5000);
      }
    };

    return () => {
      channel.close();
    };
  }, [showToast]);


  // 이메일 인증 처리 (URL 파라미터 확인)
  useEffect(() => {
    const handleEmailVerification = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const mode = urlParams.get('mode');
      const oobCode = urlParams.get('oobCode');

      // 이메일 인증 모드인지 확인
      if (mode === 'verifyEmail' && oobCode) {
        console.log('📚 이메일 인증 처리 시작...');
        
        try {
          // 이메일 인증 처리
          const result = await verifyEmailWithCode(oobCode);
          
          if (result.success) {
            console.log('✅ 이메일 인증 성공! 자동 로그인 완료');
            
            // BroadcastChannel로 다른 탭 알림
            const channel = new BroadcastChannel('email_verification');
            channel.postMessage({ type: 'EMAIL_VERIFIED' });
            channel.close();
            
            // URL에서 oobCode 파라미터 제거
            window.history.replaceState({}, document.title, '/');
            
            // 창 닫기 시도
            setTimeout(() => {
              // 현재 창이 opener가 있으면(새 창으로 열린 경우)
              if (window.opener) {
                window.close();
              } else {
                // opener가 없으면 홈페이지로 이동
                setCurrentPage('home');
                showToast('✅ 이메일 인증 완료되었습니다! 로그인되었습니다.', 'success', 5000);
              }
            }, 500);
            
          } else {
            console.error('❌ 이메일 인증 실패:', result.error);
            
            // URL에서 oobCode 파라미터 제거
            window.history.replaceState({}, document.title, '/');
            
            // 로그인 페이지로 이동
            setCurrentPage('login');
            
            // 에러 메시지 표시
            showToast(result.error || '이메일 인증에 실패했습니다.', 'error', 5000);
          }
        } catch (error) {
          console.error('❌ 이메일 인증 처리 중 오류:', error);
          
          // URL에서 oobCode 파라미터 제거
          window.history.replaceState({}, document.title, '/');
          
          // 로그인 페이지로 이동
          setCurrentPage('login');
          
          // 에러 메시지 표시
          showToast('이메일 인증 처리 중 오류가 발생했습니다.', 'error', 5000);
        }
      }
    };

    handleEmailVerification();
  }, [showToast]);



  // 실제 이메일 비밀번호 로그인
  const handleLogin = async (email, password) => {
    try {
      console.log('📚 로그인 시작...', email);

      // 1. 로그인 시도 횟수 확인
      const attemptCheck = await checkLoginAttempts(email);

      if (!attemptCheck.allowed) {
        if (attemptCheck.locked) {
          // 계정이 잠긴 경우
          return {
            success: false,
            locked: true,
            remainingMinutes: attemptCheck.remainingMinutes,
            email: email
          };
        }
        return {
          success: false,
          message: '로그인 시도 횟수를 초과했습니다.'
        };
      }

      // 2. 로그인 시도
      const user = await signIn(email, password);

      if (!user) {
        throw new Error('로그인 처리 중 오류가 발생했습니다.');
      }

      // 3. 이메일 인증 확인 (Firebase Auth 또는 Firestore 확인)
      console.log('📚 Firebase Auth 이메일 인증 상태:', user.emailVerified);

      let isEmailVerified = user.emailVerified;

      // Firebase Auth에서 미인증이면, Firestore에서 커스텀 인증 상태 확인
      if (!isEmailVerified) {
        try {
          const { getDoc, doc } = await import('firebase/firestore');
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            isEmailVerified = userData.emailVerified === true;
            console.log('📚 Firestore 이메일 인증 상태:', isEmailVerified);
          }
        } catch (firestoreError) {
          console.error('Firestore 인증 상태 확인 실패:', firestoreError);
        }
      }

      if (!isEmailVerified) {
        await logOut();
        return {
          success: false,
          emailNotVerified: true,
          email: email
        };
      }

      // 4. 로그인 성공 - 시도 횟수 초기화
      await resetLoginAttempts(email);

      console.log('로그인 성공!');

      // Analytics: 로그인 이벤트
      trackEvent('login', {
        method: 'email',
        user_id: user.uid
      });

      setCurrentPage('home');

      if (window.showToast) {
        window.showToast('✅ 로그인 성공!', 'success');
      } else {
        alert('✅ 로그인 성공!');
      }

      return { success: true };

    } catch (error) {
      console.error('\u{274C} 로그인 실패:', error);

      // 로그인 실패 기록
      const failureResult = await recordLoginFailure(email);

      let errorMessage = '';
      if (error.code === 'auth/user-not-found') {
        errorMessage = '사용자를 찾을 수 없습니다.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = '비밀번호가 일치하지 않습니다.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = '이메일 형식이 올바르지 않습니다.';
      } else if (error.code === 'auth/invalid-credential') {
        // 소셜 로그인 사용자인지 확인
        try {
          const providers = await getAuthProviders(email);
          if (providers.includes('google.com') && !providers.includes('password')) {
            errorMessage = '이 계정은 Google 로그인으로 가입되었습니다. 상단의 Google 로그인 버튼을 사용해주세요.';
          } else {
            errorMessage = '이메일 또는 비밀번호가 일치하지 않습니다.';
          }
        } catch {
          errorMessage = '이메일 또는 비밀번호가 일치하지 않습니다.';
        }
      } else {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage,
        attemptsRemaining: failureResult.attemptsRemaining,
        locked: failureResult.locked
      };
    }
  };

  // 회원가입
  const handleSignup = async (email, password, displayName) => {
    try {
      console.log('📚 회원가입 시작...', email);
      await signUp(email, password, displayName);

      console.log('✅ 회원가입 성공! 이메일 인증 필요');

      // Analytics: 회원가입 이벤트
      trackEvent('sign_up', {
        method: 'email',
        user_email: email
      });

      // 이메일 인증 페이지로 이동
      setCurrentPage('email-verification-required');
      window.tempAccountData = { email };

      return { success: true };
    } catch (error) {
      console.error('❌ 회원가입 실패:', error);

      let errorMessage = '회원가입 실패: ';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage += '이미 사용 중인 이메일입니다.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage += '비밀번호는 최소 6자 이상이어야 합니다.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage += '이메일 형식이 올바르지 않습니다.';
      } else {
        errorMessage += error.message;
      }

      return { success: false, message: errorMessage };
    }
  };

  // 로그아웃
  const handleLogout = async () => {
    try {
      await logOut();
      // setUser(null)는 onAuthStateChanged에서 자동 처리됨
      setCurrentPage('home');
      console.log('✅ 로그아웃 성공');

      if (window.showToast) {
        window.showToast('✅ 로그아웃 성공!', 'success');
      } else {
        alert('✅ 로그아웃 성공!');
      }
    } catch (error) {
      console.error('??로그아웃 실패:', error);
      alert('로그아웃 실패: ' + error.message);
    }
  };

  // 📌 영어 전문 헤더 컴포넌트
  // 로그인 페이지
  const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [attemptsRemaining, setAttemptsRemaining] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showEmailLogin, setShowEmailLogin] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsLoading(true);
      setErrorMessage('');
      setAttemptsRemaining(null);

      const result = await handleLogin(email, password);

      if (!result.success) {
        if (result.locked) {
          // 계정 잠금
          setCurrentPage('account-locked');
          window.tempAccountData = { email: result.email, remainingMinutes: result.remainingMinutes };
        } else if (result.emailNotVerified) {
          // 이메일 미인증          setCurrentPage('email-verification-required');
          window.tempAccountData = { email: result.email };
        } else {
          // 로그인 실패
          setErrorMessage(result.message);
          if (result.attemptsRemaining !== undefined) {
            setAttemptsRemaining(result.attemptsRemaining);
          }
        }
      }

      setIsLoading(false);
    };

    return (
      <div className="bb-auth-page flex items-center justify-center">
        <div className="max-w-md w-full">
          <Card className="shadow-xl border-0 bg-white/80 dark:bg-gray-800/90 backdrop-blur-sm">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="bb-auth-heading text-3xl font-bold tracking-tight">
                다시 만나 반갑습니다.</CardTitle>
              <p className="text-sm text-muted-foreground">
                영어 전문 플랫폼에 로그인하세요
              </p>
            </CardHeader>

            <CardContent>
              {/* 에러 메시지 표시 */}
              {errorMessage && (
                <Alert className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 mb-4">
                  <AlertDescription className="text-red-700 dark:text-red-300">
                    <div className="font-medium">{errorMessage}</div>
                    {attemptsRemaining !== null && attemptsRemaining > 0 && (
                      <div className="mt-2 text-sm">
                        {attemptsRemaining} 회 남았습니다.
                        {attemptsRemaining <= 2 && ' 더 실패하면 계정이 30분간 잠깁니다.'}
                      </div>
                    )}
                    {attemptsRemaining === 0 && (
                      <div className="mt-2 text-sm font-semibold">
                        다음 실패 시 계정이 30분간 잠깁니다!
                      </div>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {/* 간편 로그인 (상단에 강조) */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-center gap-2">
                  <span className="text-base font-semibold text-blue-600 dark:text-blue-400">간편 로그인</span>
                  <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">추천</span>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-14 flex items-center justify-center gap-3 border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  onClick={async () => {
                    setIsLoading(true);
                    setErrorMessage('');
                    const result = await signInWithGoogle();
                    if (result.success) {
                      await createSession(result.user.uid);
                      await validateAndManageSessions(result.user.uid, getStoredSessionToken());
                      if (result.isNewUser) {
                        showToast('Google 계정으로 가입되었습니다!', 'success');
                      } else {
                        showToast('Google 로그인 성공!', 'success');
                      }
                      setCurrentPage('home');
                    } else {
                      setErrorMessage(result.error);
                    }
                    setIsLoading(false);
                  }}
                  disabled={isLoading}
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="font-semibold text-base text-gray-700 dark:text-gray-200">Google로 로그인</span>
                </Button>

                {/* Naver 로그인 - 검수 완료 후 주석 해제
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-14 flex items-center justify-center gap-3 border-green-500 bg-green-500 hover:bg-green-600 text-white"
                  onClick={async () => {
                    setIsLoading(true);
                    setErrorMessage('');
                    try {
                      const clientId = process.env.REACT_APP_NAVER_CLIENT_ID;
                      const callbackUrl = encodeURIComponent(window.location.origin + '/auth/naver/callback');
                      const state = Math.random().toString(36).substring(7);
                      sessionStorage.setItem('naver_state', state);
                      sessionStorage.setItem('naver_redirect', 'login');
                      const naverAuthUrl = `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${clientId}&redirect_uri=${callbackUrl}&state=${state}`;
                      window.location.href = naverAuthUrl;
                    } catch (error) {
                      console.error('Naver 로그인 오류:', error);
                      setErrorMessage('Naver 로그인 중 오류가 발생했습니다.');
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading}
                >
                  <span className="font-bold text-xl">N</span>
                  <span className="font-semibold text-base">Naver로 로그인</span>
                </Button>
                */}
              </div>

              {/* 접을 수 있는 이메일 로그인 */}
              <div className="relative my-6">
                <button
                  type="button"
                  onClick={() => setShowEmailLogin(!showEmailLogin)}
                  className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors cursor-pointer"
                >
                  <span className="flex-1 border-t border-gray-300 dark:border-gray-600" />
                  <span className="px-3 flex items-center gap-1 bg-white dark:bg-gray-800">
                    이메일로 로그인
                    <span className="text-xs">{showEmailLogin ? '\u25B2' : '\u25BC'}</span>
                  </span>
                  <span className="flex-1 border-t border-gray-300 dark:border-gray-600" />
                </button>
              </div>

              {/* 이메일 로그인 폼 (접힌 상태에서 숨김) */}
              {showEmailLogin && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium leading-none dark:text-gray-200">
                    이메일
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@email.com"
                    className="h-11"
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="password" className="text-sm font-medium leading-none dark:text-gray-200">
                    비밀번호
                  </label>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="비밀번호를 입력하세요"
                    className="h-11"
                    disabled={isLoading}
                  />
                </div>

                <Button type="submit" className="w-full h-11 mt-2" disabled={isLoading}>
                  {isLoading ? '로그인 중...' : '로그인'}
                </Button>

                <div className="text-center">
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setCurrentPage('reset-password')}
                    className="text-sm text-gray-600 dark:text-gray-400"
                  >
                    비밀번호를 잊으셨나요?
                  </Button>
                </div>
              </form>
              )}

              {/* 회원가입 링크 (항상 표시) */}
              <div className="text-center pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
                <Button
                  type="button"
                  variant="link"
                  onClick={() => setCurrentPage('signup')}
                  className="text-sm"
                >
                  아직 계정이 없으신가요? 회원가입하기
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // 회원가입 페이지
  const SignupPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [passwordValidation, setPasswordValidation] = useState(null);
    const [passwordStrength, setPasswordStrength] = useState('weak');
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [agreeToPrivacy, setAgreeToPrivacy] = useState(false);
    const [agreeToAll, setAgreeToAll] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [showEmailForm, setShowEmailForm] = useState(false);

    // 비밀번호 입력 시 실시간 검증
    const handlePasswordChange = (e) => {
      const newPassword = e.target.value;
      setPassword(newPassword);

      if (newPassword) {
        const validation = validatePassword(newPassword);
        const strength = getPasswordStrength(newPassword);
        setPasswordValidation(validation);
        setPasswordStrength(strength);
      } else {
        setPasswordValidation(null);
        setPasswordStrength('weak');
      }
    };

    // 개별 약관 동의 변경 시 전체 동의 상태 자동 업데이트
    React.useEffect(() => {
      if (agreeToTerms && agreeToPrivacy) {
        setAgreeToAll(true);
      } else {
        setAgreeToAll(false);
      }
    }, [agreeToTerms, agreeToPrivacy]);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setErrorMessage('');
      setIsLoading(true);

      if (!displayName.trim()) {
        setErrorMessage('닉네임을 입력해주세요.');
        setIsLoading(false);
        return;
      }

      // 비밀번호 검증
      const validation = validatePassword(password);
      if (!validation.isValid) {
        setErrorMessage(validation.errors[0]);
        setIsLoading(false);
        return;
      }

      if (password !== confirmPassword) {
        setErrorMessage('비밀번호가 일치하지 않습니다.');
        setIsLoading(false);
        return;
      }

      // 약관 동의 검증
      if (!agreeToTerms || !agreeToPrivacy) {
        setErrorMessage('필수 약관에 모두 동의해주세요.');
        setIsLoading(false);
        return;
      }

      const result = await handleSignup(email, password, displayName);

      if (!result.success) {
        setErrorMessage(result.message);
      }

      setIsLoading(false);
    };

    return (
      <div className="bb-auth-page flex items-center justify-center">
        <div className="max-w-md w-full">
          <Card className="shadow-xl border-0 bg-white/80 dark:bg-gray-800/90 backdrop-blur-sm">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="bb-auth-heading text-3xl font-bold tracking-tight">
                {'\u{1F4DA}'} 블루북Go 회원가입
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                영어 전문 플랫폼에서 시작하세요
              </p>
              <div className="bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/50 dark:to-indigo-900/50 border border-blue-300 dark:border-blue-700 rounded-lg p-3 mt-3 space-y-2">
                <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                  {'\u{1F381}'} 회원가입시 10회 다운로드 무료 증정!
                </p>
                <p className="text-sm font-semibold text-green-700 dark:text-green-400">
                  {'\u{1F3C6}'} 온라인 테스트 3일 무료 체험!
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                  가입 즉시 모든 혜택을 이용하세요
                </p>
              </div>
            </CardHeader>

            <CardContent>
              {/* 에러 메시지 표시 */}
              {errorMessage && (
                <Alert className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 mb-4">
                  <AlertDescription className="text-red-700 dark:text-red-300">
                    {errorMessage}
                  </AlertDescription>
                </Alert>
              )}

              {/* 간편 가입 버튼들 (상단에 배치) - 강조 */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center justify-center gap-2">
                  <span className="text-base font-semibold text-blue-600 dark:text-blue-400">3초 만에 가입하기</span>
                  <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">추천</span>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-14 flex items-center justify-center gap-3 border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  onClick={async () => {
                    setIsLoading(true);
                    setErrorMessage('');
                    const result = await signInWithGoogle();
                    if (result.success) {
                      await createSession(result.user.uid);
                      await validateAndManageSessions(result.user.uid, getStoredSessionToken());
                      if (result.isNewUser) {
                        showToast('Google 계정으로 가입되었습니다!', 'success');
                      } else {
                        showToast('Google 로그인 성공!', 'success');
                      }
                      setCurrentPage('home');
                    } else {
                      setErrorMessage(result.error);
                    }
                    setIsLoading(false);
                  }}
                  disabled={isLoading}
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="font-semibold text-base text-gray-700 dark:text-gray-200">Google로 시작하기</span>
                </Button>

                {/* Kakao 로그인 - 비활성화 (2026-01-16)
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11 flex items-center justify-center gap-3 border-yellow-400 bg-yellow-300 hover:bg-yellow-400 text-gray-900"
                  onClick={async () => {
                    setIsLoading(true);
                    setErrorMessage('');
                    try {
                      const result = await signInWithKakao();
                      if (result.success) {
                        await createSession(auth.currentUser.uid);
                        await validateAndManageSessions(auth.currentUser.uid, getStoredSessionToken());
                        showToast('Kakao 계정으로 가입되었습니다!', 'success');
                        setCurrentPage('home');
                      } else {
                        setErrorMessage(result.error);
                      }
                    } catch (error) {
                      setErrorMessage('Kakao 로그인에 실패했습니다.');
                    }
                    setIsLoading(false);
                  }}
                  disabled={isLoading}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#3C1E1E" d="M12 3C6.48 3 2 6.58 2 11c0 2.84 1.87 5.33 4.67 6.75l-.95 3.56c-.08.3.26.54.52.36l4.22-2.82c.5.05 1.01.08 1.54.08 5.52 0 10-3.58 10-8s-4.48-8-10-8z"/>
                  </svg>
                  <span className="font-medium">Kakao로 시작하기</span>
                </Button>
                */}

                {/* Naver 로그인 - 검수 완료 후 주석 해제
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-14 flex items-center justify-center gap-3 border-green-500 bg-green-500 hover:bg-green-600 text-white"
                  onClick={async () => {
                    setIsLoading(true);
                    setErrorMessage('');
                    try {
                      // Naver OAuth URL로 리다이렉트
                      const clientId = process.env.REACT_APP_NAVER_CLIENT_ID;
                      const callbackUrl = encodeURIComponent(window.location.origin + '/auth/naver/callback');
                      const state = Math.random().toString(36).substring(7);
                      sessionStorage.setItem('naver_state', state);
                      sessionStorage.setItem('naver_redirect', 'signup');
                      const naverAuthUrl = `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${clientId}&redirect_uri=${callbackUrl}&state=${state}`;
                      window.location.href = naverAuthUrl;
                    } catch (error) {
                      console.error('Naver 로그인 오류:', error);
                      setErrorMessage('Naver 로그인 중 오류가 발생했습니다.');
                      setIsLoading(false);
                    }
                  }}
                  disabled={isLoading}
                >
                  <span className="font-bold text-xl">N</span>
                  <span className="font-semibold text-base">Naver로 시작하기</span>
                </Button>
                */}
              </div>

              {/* 접을 수 있는 구분선 */}
              <div className="relative my-6">
                <button
                  type="button"
                  onClick={() => setShowEmailForm(!showEmailForm)}
                  className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors cursor-pointer"
                >
                  <span className="flex-1 border-t border-gray-300 dark:border-gray-600" />
                  <span className="px-3 flex items-center gap-1 bg-white dark:bg-gray-800">
                    다른 방법으로 가입
                    <span className="text-xs">{showEmailForm ? '\u25B2' : '\u25BC'}</span>
                  </span>
                  <span className="flex-1 border-t border-gray-300 dark:border-gray-600" />
                </button>
              </div>

              {/* 이메일 가입 폼 (접힌 상태에서 숨김) */}
              {showEmailForm && (
              <>
              <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  이메일 인증이 필요합니다. 인증 메일이 스팸함에 있을 수 있습니다.
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="signup-name" className="text-sm font-medium leading-none dark:text-gray-200">
                    닉네임
                  </label>
                  <Input
                    id="signup-name"
                    type="text"
                    required
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="사용할 닉네임을 입력하세요"
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="signup-email" className="text-sm font-medium leading-none dark:text-gray-200">
                    이메일                  </label>
                  <Input
                    id="signup-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@email.com"
                    className="h-11"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="signup-password" className="text-sm font-medium leading-none dark:text-gray-200">
                      비밀번호
                    </label>
                    {password && (
                      <span className={"text-xs px-2 py-1 rounded " + getStrengthColor(passwordStrength)}>
                        {getStrengthText(passwordStrength)}
                      </span>
                    )}
                  </div>
                  <Input
                    id="signup-password"
                    type="password"
                    required
                    value={password}
                    onChange={handlePasswordChange}
                    placeholder="영문 대/소문자+숫자 조합 8자 이상"
                    className="h-11"
                  />

                  {/* 비밀번호 요구사항 표시 */}
                  {password && passwordValidation && (
                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">비밀번호 요구사항:</p>
                      <div className="space-y-1">
                        <div className={"text-xs flex items-center gap-2 " + (passwordValidation.requirements.minLength ? "text-green-600" : "text-gray-500")}>
                          {passwordValidation.requirements.minLength ? '✓' : '○'} 최소 8자 이상
                        </div>
                        <div className={"text-xs flex items-center gap-2 " + (passwordValidation.requirements.hasUpperCase ? "text-green-600" : "text-gray-500")}>
                          {passwordValidation.requirements.hasUpperCase ? '✓' : '○'} 영문 대문자 포함
                        </div>
                        <div className={"text-xs flex items-center gap-2 " + (passwordValidation.requirements.hasLowerCase ? "text-green-600" : "text-gray-500")}>
                          {passwordValidation.requirements.hasLowerCase ? '✓' : '○'} 영문 소문자 포함
                        </div>
                        <div className={"text-xs flex items-center gap-2 " + (passwordValidation.requirements.hasNumber ? "text-green-600" : "text-gray-500")}>
                          {passwordValidation.requirements.hasNumber ? '✓' : '○'} 숫자 포함
                        </div>
                        {(!passwordValidation.requirements.noSequential || !passwordValidation.requirements.noRepeating) && (
                          <>
                            {!passwordValidation.requirements.noSequential && (
                              <div className="text-xs flex items-center gap-2 text-red-600">
                                ??연속 숫자 4개 이상 사용 불가
                              </div>
                            )}
                            {!passwordValidation.requirements.noRepeating && (
                              <div className="text-xs flex items-center gap-2 text-red-600">
                                ??동일 문자 4개 이상 반복 불가
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {!password && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      예시: BlueBook2024, MyEnglish123
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label htmlFor="confirm-password" className="text-sm font-medium leading-none dark:text-gray-200">
                    비밀번호 확인
                  </label>
                  <Input
                    id="confirm-password"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="비밀번호를 다시 입력하세요"
                    className="h-11"
                  />
                </div>

                {/* 약관 동의 */}
                <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="agree-all"
                      checked={agreeToAll}
                      readOnly
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-not-allowed opacity-60 flex-shrink-0"
                    />
                    <label className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100">
                      전체 동의
                    </label>
                  </div>

                  <div className="ml-4 sm:ml-6 space-y-3">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="agree-terms"
                        checked={agreeToTerms}
                        readOnly
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-not-allowed opacity-60 flex-shrink-0"
                      />
                      <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 flex-1 min-w-0">
                        [필수] 이용약관 동의
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowTermsModal(true)}
                        className="text-[10px] sm:text-xs text-white bg-blue-600 hover:bg-blue-700 px-2 sm:px-3 py-1 rounded transition-colors whitespace-nowrap flex-shrink-0"
                      >
                        {agreeToTerms ? '✓ 완료' : '보기'}
                      </button>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="agree-privacy"
                        checked={agreeToPrivacy}
                        readOnly
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-not-allowed opacity-60 flex-shrink-0"
                      />
                      <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 flex-1 min-w-0">
                        [필수] 개인정보처리방침 동의
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowPrivacyModal(true)}
                        className="text-[10px] sm:text-xs text-white bg-blue-600 hover:bg-blue-700 px-2 sm:px-3 py-1 rounded transition-colors whitespace-nowrap flex-shrink-0"
                      >
                        {agreeToPrivacy ? '✓ 완료' : '보기'}
                      </button>
                    </div>
                  </div>

                  <div className="mt-3 p-2 sm:p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                    <p className="text-[10px] sm:text-xs text-yellow-800 dark:text-yellow-300">
                      ⚠️ <strong>필수:</strong> 각 약관의 "보기" 버튼을 클릭하여 내용을 확인하고 동의해주세요.
                    </p>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 mt-6"
                  disabled={!agreeToTerms || !agreeToPrivacy || isLoading}
                >
                  {isLoading ? '처리 중...' : '회원가입'}
                </Button>

                {/* 이메일 인증 안내 */}
                <div className="mt-4 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-[10px] sm:text-xs text-blue-800 text-center">
                    <span className="font-semibold">📧 회원가입은 이메일 인증이 필요합니다</span>
                    <br />
                    <span className="text-blue-700 mt-1 inline-block">
                      가입하면 이메일로 인증 링크가 발송되며, 링크 클릭 후 로그인이 가능합니다.
                    </span>
                  </p>
                </div>

                {/* 약관 모달 */}
                <TermsModal
                  isOpen={showTermsModal}
                  onClose={() => setShowTermsModal(false)}
                  onAgree={() => setAgreeToTerms(true)}
                />
                <PrivacyModal
                  isOpen={showPrivacyModal}
                  onClose={() => setShowPrivacyModal(false)}
                  onAgree={() => setAgreeToPrivacy(true)}
                />
                
              </form>
              </>
              )}

              {/* 로그인 페이지 이동 (항상 표시) */}
              <div className="text-center pt-4 border-t border-gray-200 dark:border-gray-700 mt-4">
                <Button
                  type="button"
                  variant="link"
                  onClick={() => setCurrentPage('login')}
                  className="text-sm"
                >
                  이미 계정이 있으신가요? 로그인하기
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // 비밀번호 재설정 요청 페이지 (이메일 입력 → 재설정 링크 발송)
  const ForgotPasswordPage = () => {
    const [email, setEmail] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsLoading(true);

      try {
        // 먼저 소셜 로그인 사용자인지 확인
        const providers = await getAuthProviders(email);
        if (providers.includes('google.com') && !providers.includes('password')) {
          showToast('이 계정은 Google 로그인으로 가입되었습니다. 로그인 페이지에서 Google 로그인 버튼을 사용해주세요.', 'error');
          setIsLoading(false);
          return;
        }

        const { resetPassword } = await import('./firebase');
        await resetPassword(email);
        setIsSubmitted(true);
        showToast('비밀번호 재설정 이메일이 발송되었습니다! 이메일함을 확인해주세요.', 'success');
      } catch (error) {
        console.error('비밀번호 재설정 실패:', error);
        if (error.code === 'auth/user-not-found') {
          showToast('등록되지 않은 이메일입니다.', 'error');
        } else if (error.code === 'auth/invalid-email') {
          showToast('유효하지 않은 이메일 형식입니다.', 'error');
        } else {
          showToast('비밀번호 재설정 이메일 발송에 실패했습니다.', 'error');
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (isSubmitted) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
              <CardHeader className="space-y-1 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl"></span>
                </div>
                <CardTitle className="text-2xl font-bold">
                  이메일 발송 완료
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <p className="text-gray-700">
                  <strong>{email}</strong>로<br />
                  비밀번호 재설정 링크를 발송했습니다.
                </p>
                <p className="text-sm text-gray-600">
                  이메일함을 확인하고 링크를 클릭하여 새로운 비밀번호를 설정하세요.
                </p>
                <div className="pt-4">
                  <Button
                    onClick={() => setCurrentPage('login')}
                    className="w-full"
                  >
                    로그인 페이지로 돌아가기                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="bb-auth-heading text-3xl font-bold tracking-tight">
                🔐 비밀번호 재설정              </CardTitle>
              <p className="text-sm text-muted-foreground">
                가입하신 이메일 주소를 입력하세요.              </p>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="reset-email" className="text-sm font-medium leading-none">
                    이메일                  </label>
                  <Input
                    id="reset-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@email.com"
                    className="h-11"
                    disabled={isLoading}
                  />
                  <p className="text-xs text-gray-500">
                    비밀번호 재설정 링크를 이메일로 보내드립니다.
                  </p>
                </div>

                <Button type="submit" className="w-full h-11 mt-6" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      전송 중...
                    </>
                  ) : (
                    '재설정 링크 보내기'
                  )}
                </Button>

                <div className="text-center pt-4">
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setCurrentPage('login')}
                    className="text-sm"
                    disabled={isLoading}
                  >
                    로그인 페이지로 돌아가기                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // 이메일 인증 필수 페이지
  const EmailVerificationRequiredPage = () => {
    const [isSending, setIsSending] = useState(false);
    const [message, setMessage] = useState('');
    const accountData = window.tempAccountData || {};

    const handleResendEmail = async () => {
      setIsSending(true);
      setMessage('');

      try {
        await resendVerificationEmail();
        setMessage('인증 이메일이 재전송되었습니다. 이메일함을 확인해주세요.');
      } catch (error) {
        console.error('이메일 재전송 실패:', error);
        setMessage('이메일 재전송에 실패했습니다. 다시 시도해주세요.');
      } finally {
        setIsSending(false);
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="space-y-1 text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">📚</span>
              </div>
              <CardTitle className="text-2xl font-bold">
                이메일 인증이 필요합니다              </CardTitle>
            </CardHeader>

            <CardContent className="text-center space-y-4">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                <p className="text-gray-700 mb-2">
                  <strong className="text-blue-700">{accountData.email}</strong>로<br />
                  인증 이메일을 발송했습니다.
                </p>
                <p className="text-sm text-gray-600">
                  회원가입이 거의 완료되었습니다! 📚
                </p>
              </div>

              <Alert className="border-blue-200 bg-blue-50 text-left">
                <AlertDescription className="text-blue-700 text-sm">
                  <div className="space-y-3">
                    <p className="font-bold text-base">📧 다음 단계를 진행해주세요</p>
                    <ol className="list-decimal list-inside space-y-2 pl-2">
                      <li className="font-medium">
                        이메일함에서 <span className="text-blue-800">"Bluebook Go"</span>에서 온 메일 확인
                      </li>
                      <li className="font-medium">
                        메일 본문의 <span className="text-blue-800">"이메일 인증하기"</span> 버튼 클릭
                      </li>
                      <li className="font-medium">
                        인증 완료 후 아래 <span className="text-blue-800">"로그인 페이지로 돌아가기</span> 버튼을 눌러 로그인                      </li>
                    </ol>

                    <div className="mt-4 pt-3 border-t border-blue-200">
                      <p className="text-xs font-medium mb-2">⚠️ 이메일이 보이지 않나요?</p>
                      <ul className="text-xs space-y-1 pl-4">
                        <li>??<strong>스팸함</strong>을 확인해보세요</li>
                        <li>??<strong>프로모션</strong> 탭을 확인해보세요 (Gmail)</li>
                        <li>??잠시 기다려보세요</li>
                        <li></li>
                      </ul>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>

              {message && (
                <Alert className={message.includes('실패') ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}>
                  <AlertDescription className={message.includes('실패') ? 'text-red-700' : 'text-green-700'}>
                    {message}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-3 pt-4">
                <Button
                  onClick={handleResendEmail}
                  variant="outline"
                  className="w-full"
                  disabled={isSending}
                >
                  {isSending ? '전송 중...' : '인증 이메일 재전송'}
                </Button>

                <Button
                  onClick={() => setCurrentPage('login')}
                  className="w-full"
                >
                  로그인 페이지로 돌아가기
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // 계정 잠금 페이지
  const AccountLockedPage = () => {
    const [isUnlocking, setIsUnlocking] = useState(false);
    const [message, setMessage] = useState('');
    const accountData = window.tempAccountData || { remainingMinutes: 30 };

    const handleUnlockEmail = async () => {
      setIsUnlocking(true);
      setMessage('');

      try {
        // 이메일로 잠금 해제 링크 전송 (비밀번호 재설정 이메일 사용)
        await resetPassword(accountData.email);
        setMessage('계정 잠금 해제 링크가 이메일로 전송되었습니다!');
      } catch (error) {
        console.error('잠금 해제 이메일 전송 실패:', error);
        setMessage('이메일 전송에 실패했습니다. 다시 시도해주세요.');
      } finally {
        setIsUnlocking(false);
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="space-y-1 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">📚</span>
              </div>
              <CardTitle className="text-2xl font-bold text-red-700">
                계정이 일시적으로 잠겼습니다              </CardTitle>
            </CardHeader>

            <CardContent className="text-center space-y-4">
              <p className="text-gray-700">
                보안을 위해 5회 이상 로그인에 실패하여<br />
                계정이 <strong className="text-red-600">30분간 잠금</strong>되었습니다.
              </p>

              <Alert className="border-orange-200 bg-orange-50 text-left">
                <AlertDescription className="text-orange-700 text-sm">
                  <div className="space-y-2">
                    <p className="font-medium">⏰ 남은 시간: {accountData.remainingMinutes}분</p>
                    <p className="text-xs mt-2">
                      {accountData.remainingMinutes}분 후 자동으로 잠금이 해제됩니다.
                    </p>
                  </div>
                </AlertDescription>
              </Alert>

              <Alert className="border-blue-200 bg-blue-50 text-left">
                <AlertDescription className="text-blue-700 text-sm">
                  <div className="space-y-2">
                    <p className="font-medium">📧 즉시 해제 방법:</p>
                    <p>아래 버튼을 클릭하여 이메일로 즉시 잠금을 해제할 수 있습니다.</p>
                  </div>
                </AlertDescription>
              </Alert>

              {message && (
                <Alert className={message.includes('실패') ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}>
                  <AlertDescription className={message.includes('실패') ? 'text-red-700' : 'text-green-700'}>
                    {message}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-3 pt-4">
                <Button
                  onClick={handleUnlockEmail}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={isUnlocking}
                >
                  {isUnlocking ? '전송 중...' : '📧 이메일로 즉시 잠금 해제하기'}
                </Button>

                <Button
                  onClick={() => setCurrentPage('login')}
                  variant="outline"
                  className="w-full"
                >
                  로그인 페이지로 돌아가기                </Button>

                <Button
                  onClick={() => setCurrentPage('reset-password')}
                  variant="link"
                  className="w-full text-sm text-gray-600"
                >
                  비밀번호를 잊으셨나요?
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // 영어 구독 결제 페이지
  const PricingPage = () => (
    <div className="bb-page bb-pricing-page">
      <div className="bb-page-inner">
        {/* 요약 및 간단한 설명 */}
        <PageHeading eyebrow="PLANS & MEMBERSHIP" title="나에게 맞는 이용 방법." description="수업에 필요한 자료부터 온라인 학습까지, 필요한 범위에 맞춰 선택하세요." />
        <div className="bb-pricing-benefits"><div><BookOpen/><span><strong>온라인 문제풀이</strong>직접 풀고 해설로 이해하세요</span></div><div><Target/><span><strong>학습 기록과 복습</strong>진행 상황과 오답을 확인하세요</span></div><div><FileText/><span><strong>PDF 수업 자료</strong>수업과 오프라인 학습을 준비하세요</span></div></div>

        {/* 요금제 카드 - 가격표로 보여주기 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12 max-w-4xl mx-auto">
          {englishPlans.map((plan) => (
            <div
              key={plan.id}
              className={"bb-plan-card relative bg-white border border-gray-200 " + (plan.popular ? "bb-plan-popular" : "")}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    추천 플랜
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{plan.name}</h3>

                {appliedPromo ? (
                  <div>
                    <div className="text-lg text-gray-400 dark:text-gray-500 line-through mb-1">
                      {plan.originalPrice.toLocaleString()}원
                    </div>
                    <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                      {getDiscountedPrice(plan).toLocaleString()}
                      <span className="text-lg font-normal text-gray-500 dark:text-gray-400">원</span>
                    </div>
                    <div className="inline-block bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 text-sm font-bold px-3 py-1 rounded-full mb-2">
                      {Math.round((1 - getDiscountedPrice(plan) / plan.originalPrice) * 100)}% 할인
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="text-4xl font-bold text-gray-900 dark:text-white mb-1">
                      {plan.price.toLocaleString()}
                      <span className="text-lg font-normal text-gray-500 dark:text-gray-400">원</span>
                    </div>
                  </div>
                )}

                <p className="text-gray-600 dark:text-gray-300 mt-2">
                  {plan.duration === 999999 ? '평생 이용권' : Math.floor(plan.duration / 30) + '개월 이용권'}
                </p>
              </div>

              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <div className="w-5 h-5 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full"></div>
                    </div>
                    <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => {
                  if (!user) {
                    alert('로그인이 필요합니다.');
                    setCurrentPage('login');
                    return;
                  }

                  // 중복 결제 방지 로직
                  // 체험(trial) 또는 가입 보너스(signup_bonus) 사용자는 유료 결제 허용
                  const currentPlanType = subscription?.planType?.toLowerCase() || '';
                  const isTrialOrBonus = ['trial', 'signup_bonus', ''].includes(currentPlanType) || !subscription?.planType;

                  if (hasSubscription && !isTrialOrBonus) {
                    const daysLeft = getDaysRemaining();
                    const currentPlanName = getDisplayPlanName();

                    // 같은 플랜 선택 시
                    if (currentPlanName === plan.name) {
                      if (daysLeft > 30) {
                        alert(`이미 ${plan.name} 플랜을 구독 중입니다.\n\n만료 30일 전부터 연장이 가능합니다.\n(현재 D-${daysLeft}, ${daysLeft - 30}일 후 연장 가능)`);
                        return;
                      }
                      // 30일 이하면 연장 진행 (기존 만료일 + 새 기간)
                    } else {
                      // 다른 플랜 선택 시
                      alert(`현재 ${currentPlanName} 플랜을 구독 중입니다.\n\n플랜 변경은 현재 구독 만료 후 가능합니다.\n(D-${daysLeft}일 남음)`);
                      return;
                    }
                  }

                  console.log('플랜 선택:', plan);

                  // Analytics: 플랜 선택 이벤트
                  trackEvent('select_plan', {
                    plan_id: plan.id,
                    plan_name: plan.name,
                    price: appliedPromo ? getDiscountedPrice(plan) : plan.price,
                    original_price: plan.price,
                    has_promo: !!appliedPromo,
                    promo_name: appliedPromo?.name || 'none'
                  });

                  setSelectedPlan(plan);
                  setCurrentPage('payment');
                }}
                className={"w-full " + (plan.popular ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-900 hover:bg-gray-800 dark:bg-slate-600 dark:hover:bg-slate-500")}
                size="lg"
              >
                선택하기
              </Button>
            </div>
          ))}
        </div>

        {/* 프로모션 코드 - 항상 펼쳐진 카드 형태 */}
        <div className="max-w-3xl mx-auto mb-4">
          <div className="border border-orange-200 dark:border-orange-800 rounded-lg px-4 py-4 bg-white dark:bg-slate-800">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xl">🎫</span>
              <span className="font-bold text-orange-900 dark:text-orange-300">프로모션 코드가 있으신가요?</span>
            </div>
            <p className="text-sm text-orange-700 dark:text-orange-400 mb-4">
              특별 할인 또는 플랜 부여 코드를 입력하세요.
            </p>
            <div className="flex gap-2">
              <input
                ref={promoCodeInputRef}
                type="text"
                placeholder="프로모션 코드 입력"
                onChange={(e) => {
                  e.target.value = e.target.value.toUpperCase();
                  if (promoError) setPromoError('');
                }}
                className="flex-1 font-mono px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent uppercase bg-white dark:bg-slate-700 text-gray-900 dark:text-white"
                disabled={!!appliedPromo}
              />
              {appliedPromo ? (
                <Button
                  onClick={() => {
                    setAppliedPromo(null);
                    if (promoCodeInputRef.current) promoCodeInputRef.current.value = '';
                    setPromoError('');
                  }}
                  variant="outline"
                  className="border-orange-600 text-orange-600 hover:bg-orange-50"
                >
                  취소
                </Button>
              ) : (
                <Button
                  onClick={() => applyPromoCode(promoCodeInputRef.current?.value || '')}
                  variant="default"
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  사용
                </Button>
              )}
            </div>
            {appliedPromo && (
              <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
                <p className="text-sm text-green-800 dark:text-green-300 font-medium">
                  ✅ {appliedPromo.name} 프로모션이 적용되었습니다!
                </p>
              </div>
            )}
            {promoError && (
              <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-800 dark:text-red-300 font-medium">
                  ❌ {promoError}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* 친구 추천 코드 - Accordion으로 접어서 표시 */}
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full space-y-4">

            {/* 친구 추천 코드 (로그인 안한 경우) */}
            {!user && (
              <AccordionItem value="referral" className="border border-purple-200 dark:border-purple-800 rounded-lg px-4 bg-white dark:bg-slate-800">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🎁</span>
                    <span className="font-bold text-purple-900 dark:text-purple-300">친구 추천 코드가 있으신가요?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pt-2">
                    <p className="text-sm text-purple-700 dark:text-purple-400 mb-4">
                      {isSeasonEvent()
                        ? '🔥 2배 이벤트! 가입 시 프리미엄 6일!'
                        : '가입 시 프리미엄 3일! 단계별 보상도!'}
                    </p>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="BLUEBOOKXXXXXX"
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                        className="flex-1 font-mono"
                      />
                      <Button
                        onClick={() => applyReferralCode(referralCode)}
                        variant="default"
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        적용
                      </Button>
                    </div>
                    {appliedReferral && (
                      <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
                        <p className="text-sm text-green-800 dark:text-green-300 font-medium">
                          ✅ {appliedReferral.referrerName}님의 추천 코드가 적용되었습니다!
                        </p>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* 내 추천 코드 (로그인한 경우) */}
            {user && myReferralCode && (
              <AccordionItem value="my-referral" className="border border-purple-200 dark:border-purple-800 rounded-lg px-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🤝</span>
                    <span className="font-bold text-purple-900 dark:text-purple-300">친구 초대하기</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="pt-2">
                    <div className="bg-white dark:bg-slate-800 rounded-lg p-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">내 추천 코드</p>
                      <p className="font-mono font-bold text-2xl text-purple-600 dark:text-purple-400 mb-3">{myReferralCode}</p>

                      {/* 카카오톡 공유 버튼 */}
                      <Button
                        onClick={() => shareToKakao(myReferralCode)}
                        size="sm"
                        className="w-full mb-3 flex items-center justify-center gap-2"
                        style={{ backgroundColor: '#FEE500', color: '#000000' }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 3C6.477 3 2 6.463 2 10.714c0 2.756 1.833 5.177 4.593 6.544-.193.704-.698 2.55-.8 2.943-.128.493.18.486.38.354.157-.104 2.5-1.7 3.537-2.39.755.105 1.534.161 2.29.161 5.523 0 10-3.463 10-7.612C22 6.463 17.523 3 12 3z"/>
                        </svg>
                        <span className="font-medium">카카오톡 공유</span>
                      </Button>

                      {/* 공유 버튼들 */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                        <Button
                          onClick={() => copyShareText(myReferralCode)}
                          size="sm"
                          className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white flex items-center justify-center gap-2"
                        >
                          <span>💬</span>
                          <span>공유 메시지 복사</span>
                        </Button>
                        <Button
                          onClick={() => copyShareLink(myReferralCode)}
                          variant="outline"
                          size="sm"
                          className="border-purple-300 dark:border-purple-600 text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 flex items-center justify-center gap-2"
                        >
                          <span>🔗</span>
                          <span>링크 복사</span>
                        </Button>
                      </div>
                      <Button
                        onClick={() => {
                          navigator.clipboard.writeText(myReferralCode);
                          showToast('추천 코드가 복사되었습니다!', 'success');
                        }}
                        variant="outline"
                        size="sm"
                        className="w-full border-purple-300 dark:border-purple-600 text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 flex items-center justify-center gap-2"
                      >
                        <span>📋</span>
                        <span>코드만 복사</span>
                      </Button>

                      <p className="text-xs text-gray-600 dark:text-gray-400 bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded border border-yellow-200 dark:border-yellow-700 mt-3">
                        💡 친구가 회원가입 시 추천 코드를 입력하면 서로에게 혜택이 있어요!
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </div>
      </div>
    </div>
  );

  // 기존의 ProfilePage, UploadPage 등은 그대로 유지...
  const ProfilePage = () => {
    const formatDate = (dateString) => {
      if (!dateString) return '정보 없음';
      return new Date(dateString).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">📈 영어 학습 현황</h1>

          {user && <AdminSetup user={user} />}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">💳 현재 구독 상태</h2>
                
                {subscriptionLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">구독 정보를 불러오는 중...</p>
                  </div>
                ) : hasSubscription ? (
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">플랜</span>
                        <span className="font-medium">{planName || '정보 없음'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">남은 영어자료 다운로드</span>
                        <span className={"font-medium " + (downloadsRemaining <= 10 ? "text-red-600" : downloadsRemaining <= 30 ? "text-yellow-600" : "text-blue-600")}>
                          {downloadsRemaining} / {totalDownloads}건
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">만료일</span>
                        <span className={"font-medium " + (getDaysRemaining() <= 7 ? "text-red-600" : getDaysRemaining() <= 30 ? "text-yellow-600" : "text-gray-900")}>
                          {formatDate(subscription.expiryDate)}
                          {getDaysRemaining() > 0 && (
                            <span className="text-sm text-gray-500 ml-2">
                              ({getDaysRemaining()}일 남음)
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">상태</span>
                        <Badge 
                          variant={
                            isActive ? 'default' : 
                            isExpired ? 'destructive' : 
                            'secondary'
                          }
                          className="text-xs"
                        >
                          {isActive ? '✅ 활성' : isExpired ? '❌ 만료' : '⏸️ 비활성'}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span>사용량</span>
                        <span>{getUsagePercentage()}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={"h-3 rounded-full transition-all duration-300 " + (getUsagePercentage() >= 90 ? "bg-red-500" : getUsagePercentage() >= 70 ? "bg-yellow-500" : "bg-blue-600")}
                          style={{width: getUsagePercentage() + "%"}}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="mt-6">
                      {isExpired || getDaysRemaining() <= 7 ? (
                        <Button 
                          onClick={() => setCurrentPage('pricing')}
                          className="w-full bg-red-600 hover:bg-red-700"
                          size="lg"
                        >
                          🚨 긴급 갱신 필요
                        </Button>
                      ) : getDaysRemaining() <= 30 ? (
                        <Button 
                          onClick={() => setCurrentPage('pricing')}
                          className="w-full bg-yellow-600 hover:bg-yellow-700"
                          size="lg"
                        >
                          ⚠️ 플랜 연장하기
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => setCurrentPage('pricing')}
                          className="w-full"
                          size="lg"
                        >
                          ⬆️ 플랜 업그레이드                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">📋</span>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">구독 플랜이 없습니다</h3>
                    <p className="text-gray-600 mb-6">
                      영어 전문 자료를 다운로드하려면 구독이 필요합니다.
                    </p>
                    <Button
                      onClick={() => setCurrentPage('pricing')}
                      size="lg"
                    >
                      🛒 구독 플랜 선택하기
                    </Button>
                  </div>
                )}
              </div>

              {hasSubscription && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">📝 학습 이용 내역</h2>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">구독 시작</span>
                      <span className="font-medium">{formatDate(subscription.createdAt)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">마지막 다운로드</span>
                      <span className="font-medium">
                        {subscription.lastDownloadAt ? formatDate(subscription.lastDownloadAt) : '없음'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">총 이용한 자료</span>
                      <span className="font-medium">{totalDownloads - downloadsRemaining}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">👤 계정 정보</h2>
                <div className="space-y-3">
                  <div>
                    <span className="text-gray-600">이메일</span>
                    <p className="font-medium">{user?.email || 'user@example.com'}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">가입일</span>
                    <p className="font-medium">{formatDate(user?.metadata?.creationTime)}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">최근 로그</span>
                    <p className="font-medium">{formatDate(user?.metadata?.lastSignInTime)}</p>
                  </div>
                </div>
              </div>

              {hasSubscription && (
                <Alert 
                  variant={
                    isExpired || getDaysRemaining() <= 7 ? 'destructive' : 'default'
                  }
                  className={
                    isExpired ? 'border-red-200 bg-red-50' :
                    getDaysRemaining() <= 7 ? 'border-red-200 bg-red-50' :
                    getDaysRemaining() <= 30 ? 'border-amber-200 bg-amber-50' :
                    downloadsRemaining <= 10 ? 'border-yellow-200 bg-yellow-50' :
                    'border-green-200 bg-green-50'
                  }
                >
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-xl">
                      {isExpired ? '⚠️' :
                      getDaysRemaining() <= 7 ? '🔴' :
                      getDaysRemaining() <= 30 ? '🟡' :
                      downloadsRemaining <= 10 ? '⚠️' : '✅'}
                    </span>
                    <AlertTitle className={
                      isExpired ? 'text-red-800' :
                      getDaysRemaining() <= 7 ? 'text-red-800' :
                      getDaysRemaining() <= 30 ? 'text-amber-800' :
                      downloadsRemaining <= 10 ? 'text-yellow-800' : 'text-green-800'
                    }>
                      {isExpired ? '구독 만료' :
                      getDaysRemaining() <= 7 ? '곧 만료 예정' :
                      getDaysRemaining() <= 30 ? '만료 예정' :
                      downloadsRemaining <= 10 ? '다운로드 부족' : '정상 사용중'}
                    </AlertTitle>
                  </div>
                  
                  <AlertDescription className={
                    isExpired ? 'text-red-700' :
                    getDaysRemaining() <= 7 ? 'text-red-700' :
                    getDaysRemaining() <= 30 ? 'text-amber-700' :
                    downloadsRemaining <= 10 ? 'text-yellow-700' : 'text-green-700'
                  }>
                    {isExpired ? '구독이 만료되었습니다. 갱신해주세요.' :
                    getDaysRemaining() <= 7 ? getDaysRemaining() + '일 후 만료됩니다!' :
                    getDaysRemaining() <= 30 ? getDaysRemaining() + '일 후 만료됩니다.' :
                    downloadsRemaining <= 10 ? '영어 자료 ' + downloadsRemaining + '건만 남았습니다!' :
                    '영어 학습을 활발하게 진행하고 있습니다. (' + getDaysRemaining() + '일 남음)'}
                    
                    {(isExpired || getDaysRemaining() <= 30 || downloadsRemaining <= 10) && (
                      <Button
                        onClick={() => setCurrentPage('pricing')}
                        className={"w-full mt-3 h-9 text-sm " + (isExpired || getDaysRemaining() <= 7 ? "bg-red-600 hover:bg-red-700" : getDaysRemaining() <= 30 ? "bg-amber-600 hover:bg-amber-700" : "bg-yellow-600 hover:bg-yellow-700")}
                      >
                        {isExpired ? '지금 갱신하기' : '미리 연장하기'}
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // UploadPage는 기존 코드 유지하되 영어 전문으로 설정 업데이트
  const UploadPage = () => {
    const [activeTab, setActiveTab] = useState('upload');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [storageStats, setStorageStats] = useState({ used: 0, total: 5, files: 0 });
    const [filterSubject, setFilterSubject] = useState('all');
    const [filterDifficulty, setFilterDifficulty] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [uploadSettings, setUploadSettings] = useState({
      defaultSubject: 'English',
      defaultDifficulty: '중',
      autoRefresh: true,
      maxFileSize: 2 // MB
    });

    const STORAGE_KEY = 'uploadedPDFsV2';
    const englishSubjects = ['English Grammar', 'English Exam', 'English Suneung', 'English Original'];
    const difficulties = ['상', '중', '하'];

    // 업로드된 파일 목록 가져오기
    useEffect(() => {
      loadUploadedFiles();
      updateStorageStats();
    }, []);

    const loadUploadedFiles = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        const files = stored ? JSON.parse(stored) : [];
        setUploadedFiles(files);
      } catch (error) {
        console.error('파일 목록 로드 실패:', error);
        setUploadedFiles([]);
      }
    };

    const updateStorageStats = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        const files = stored ? JSON.parse(stored) : [];
        const usedBytes = new Blob([stored || '']).size;
        const usedMB = (usedBytes / 1024 / 1024).toFixed(2);
        
        setStorageStats({
          used: usedMB,
          total: 5,
          files: files.length
        });
      } catch (error) {
        console.error('스토리지 정보 업데이트 실패:', error);
      }
    };

    // 파일 삭제
    const deleteFile = async (fileId) => {
      if (!window.confirm('정말 이 파일을 삭제하시겠습니까?')) {
        return;
      }

      try {
        const updatedFiles = uploadedFiles.filter(file => file.id !== fileId);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedFiles));
        setUploadedFiles(updatedFiles);
        updateStorageStats();
        
        setMessage('??파일이 삭제되었습니다!');
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        console.error('파일 삭제 실패:', error);
        setMessage('??파일 삭제에 실패했습니다.');
        setTimeout(() => setMessage(''), 3000);
      }
    };

    // 파일 정보 수정
    const updateFileInfo = (fileId, updates) => {
      try {
        const updatedFiles = uploadedFiles.map(file => 
          file.id === fileId ? { ...file, ...updates, updatedAt: new Date().toISOString() } : file
        );
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedFiles));
        setUploadedFiles(updatedFiles);
        
        setMessage('??파일 정보가 업데이트되었습니다!');
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        console.error('파일 정보 업데이트 실패:', error);
      }
    };

    // 전체 파일 삭제
    const clearAllFiles = () => {
      if (!window.confirm('모든 업로드된 파일을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
        return;
      }

      localStorage.removeItem(STORAGE_KEY);
      setUploadedFiles([]);
      updateStorageStats();
      
      setMessage('📚�?모든 파일이 삭제되었습니다!');
      setTimeout(() => setMessage(''), 3000);
    };

    // 필터링된 파일 목록
    const filteredFiles = uploadedFiles.filter(file => {
      const matchesSubject = filterSubject === 'all' || file.subject === filterSubject;
      const matchesDifficulty = filterDifficulty === 'all' || file.difficulty === filterDifficulty;
      const matchesSearch = searchTerm === '' || 
        file.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        file.name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSubject && matchesDifficulty && matchesSearch;
    });

    // 업로드 성공 핸들러
    const handleUploadSuccess = (uploadedFile) => {
      console.log('파일 업로드 성공:', uploadedFile);
      loadUploadedFiles();
      updateStorageStats();
      setMessage('??파일 업로드가 완료되었습니다!');
      setTimeout(() => setMessage(''), 3000);
    };

    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* 헤더 */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">📤 파일 업로드 센터</h1>
            <p className="text-gray-600">영어 전문 PDF 파일 업로드, 관리, 설정을 한곳에서 처리하세요</p>
          </div>

          {/* 스토리지 상태 요약 */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
                <div className="flex items-center justify-center gap-3">
                  <span className="text-2xl">💾</span>
                  <div>
                    <div className="text-lg font-bold text-blue-900">{storageStats.used}MB</div>
                    <div className="text-sm text-blue-700">사용량</div>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-6 h-6 text-green-600" />
                  <div>
                    <div className="text-lg font-bold text-green-900">{storageStats.files}</div>
                    <div className="text-sm text-green-700">파일</div>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-3">
                  <span className="text-2xl">📈</span>
                  <div>
                    <div className="text-lg font-bold text-purple-900">
                      {Math.round((parseFloat(storageStats.used) / storageStats.total) * 100)}%
                    </div>
                    <div className="text-sm text-purple-700"></div>
                  </div>
                </div>
                
                <div className="flex items-center justify-center gap-3">
                  <Calendar className="w-6 h-6 text-orange-600" />
                  <div>
                    <div className="text-lg font-bold text-orange-900">
                      {(storageStats.total - parseFloat(storageStats.used)).toFixed(1)}MB
                    </div>
                    <div className="text-sm text-orange-700">여유공간</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shadcn Tabs로 개선된 업로드 인터페이스*/}
          <Card className="shadow-sm border border-gray-200">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <CardHeader className="pb-4">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="upload" className="flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    <span className="hidden sm:inline">파일 업로드</span>
                    <span className="sm:hidden">업로드</span>
                  </TabsTrigger>
                  <TabsTrigger value="manage" className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    <span className="hidden sm:inline">업로드??관</span>
                    <span className="sm:hidden">관</span>
                  </TabsTrigger>
                  <TabsTrigger value="stats" className="flex items-center gap-2">
                    <span className="text-lg">📊</span>
                    <span className="hidden sm:inline">통계</span>
                    <span className="sm:hidden">통계</span>
                  </TabsTrigger>
                  <TabsTrigger value="settings" className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    <span className="hidden sm:inline">설정</span>
                    <span className="sm:hidden">설정</span>
                  </TabsTrigger>
                </TabsList>
              </CardHeader>

              <CardContent className="pt-0">
                {/* 파일 업로드 탭*/}
                <TabsContent value="upload" className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">🏠 홈파일 업로드</h2>
                    
                    {/* 현재 설정 표시 */}
                    <Card className="mb-6">
                      <CardHeader>
                        <CardTitle className="text-lg">⚠️ 현재 업로드 설정</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">기본 과목:</span>
                            <Badge variant="outline">{uploadSettings.defaultSubject}</Badge>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">기본 사이즈</span>
                            <Badge variant="outline">{uploadSettings.defaultDifficulty}</Badge>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">최�? 인기:</span>
                            <Badge variant="outline">{uploadSettings.maxFileSize}MB</Badge>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">자동 새로고침:</span>
                            <Badge variant={uploadSettings.autoRefresh ? "default" : "secondary"}>
                              {uploadSettings.autoRefresh ? '??ON' : '??OFF'}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* OptimizedFileUpload 컴포넌트 */}
                    <OptimizedFileUpload 
                      onUploadSuccess={handleUploadSuccess}
                    />
                  </div>
                </TabsContent>

                {/* 업로드??관�???*/}
                <TabsContent value="manage" className="space-y-6">
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-semibold text-gray-900"></h2>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            loadUploadedFiles();
                            updateStorageStats();
                          }}
                          variant="outline"
                          size="sm"
                        >
                          🔄 업로드고침
                        </Button>
                        
                        {uploadedFiles.length > 0 && (
                          <Button
                            onClick={clearAllFiles}
                            variant="destructive"
                            size="sm"
                          >
                            ⚠️ 전체 삭제
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* 필터 및 검색*/}
                    <Card className="mb-6">
                      <CardContent className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">🔍 검</label>
                            <div className="relative">
                              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                              <input
                                type="text"
                                placeholder="파일명으�?검??.."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-2">📂 과목 필터</label>
                            <select
                              value={filterSubject}
                              onChange={(e) => setFilterSubject(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="all">전체 과목</option>
                              {englishSubjects.map(subject => (
                                <option key={subject} value={subject}>{subject}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium mb-2">�?사이즈 필터</label>
                            <select
                              value={filterDifficulty}
                              onChange={(e) => setFilterDifficulty(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="all">전체 사이즈</option>
                              {difficulties.map(diff => (
                                <option key={diff} value={diff}>{diff}</option>
                              ))}
                            </select>
                          </div>
                          
                          <div className="flex items-end">
                            <Button
                              onClick={() => {
                                setSearchTerm('');
                                setFilterSubject('all');
                                setFilterDifficulty('all');
                              }}
                              variant="outline"
                              size="sm"
                              className="w-full"
                            >
                              필터 초기화                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* 파일 목록 */}
                    {filteredFiles.length > 0 ? (
                      <div className="space-y-4">
                        <div className="text-sm text-gray-600 mb-4">
                          총 {uploadedFiles.length}개 중 {filteredFiles.length}개 파일 표시
                        </div>
                        
                        {filteredFiles.map((file) => (
                          <Card key={file.id} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start">
                                <div className="flex-1 min-w-0 mr-4">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h3 className="font-semibold text-gray-900 truncate">
                                      {file.title || file.name}
                                    </h3>
                                    <Badge variant="outline" className="text-xs">
                                      {file.subject || 'English'}
                                    </Badge>
                                    <Badge
                                      variant={
                                        file.difficulty === '상' ? 'destructive' :
                                        file.difficulty === '중' ? 'default' :
                                        'secondary'
                                      }
                                      className="text-xs"
                                    >
                                      {file.difficulty || '중'}
                                    </Badge>
                                  </div>
                                  
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-gray-500">
                                    <div>📚 {file.uploadDate}</div>
                                    <div>📚 {file.formattedSize}</div>
                                    <div>⬇️ {file.downloads || 0}</div>
                                    <div>📚 {file.uploadTime}</div>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  <Button
                                    onClick={() => {
                                      const newTitle = prompt('새 제목을 입력하세요:', file.title);
                                      if (newTitle && newTitle !== file.title) {
                                        updateFileInfo(file.id, { title: newTitle });
                                      }
                                    }}
                                    variant="outline"
                                    size="sm"
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  
                                  <Button
                                    onClick={() => deleteFile(file.id)}
                                    variant="destructive"
                                    size="sm"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    ) : uploadedFiles.length === 0 ? (
                      <Card>
                        <CardContent className="text-center py-12">
                          <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">업로드된 파일이 없습니다</h3>
                          <p className="text-gray-600 mb-6">
                            첫 번째 PDF 파일을 업로드해보세요.
                          </p>
                          <Button 
                            onClick={() => setActiveTab('upload')}
                            size="lg"
                          >
                            📚 파일 업로드하기                          </Button>
                        </CardContent>
                      </Card>
                    ) : (
                      <Card>
                        <CardContent className="text-center py-8">
                          <p className="text-gray-600">필터 조건에 맞는 파일이 없습니다.</p>
                          <Button 
                            onClick={() => {
                              setSearchTerm('');
                              setFilterSubject('all');
                              setFilterDifficulty('all');
                            }}
                            variant="outline"
                            className="mt-4"
                          >
                            필터 초기화                          </Button>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </TabsContent>

                {/* 통계 ??*/}
                <TabsContent value="stats" className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">📚 파일 통계 �?분석</h2>
                    
                    {/* 스토리지 사용량 상세 */}
                    <Card className="mb-6">
                      <CardHeader>
                        <CardTitle>📊 저장소 사용량</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">사용량</span>
                            <span className={"font-bold " + (parseFloat(storageStats.used) > 4 ? "text-red-600" : parseFloat(storageStats.used) > 3 ? "text-yellow-600" : "text-green-600")}>
                              {storageStats.used}MB / {storageStats.total}MB
                            </span>
                          </div>

                          <div className="w-full bg-gray-200 rounded-full h-4">
                            <div
                              className={"h-4 rounded-full transition-all duration-300 " + (parseFloat(storageStats.used) > 4 ? "bg-red-500" : parseFloat(storageStats.used) > 3 ? "bg-yellow-500" : "bg-green-500")}
                              style={{ width: Math.min((parseFloat(storageStats.used) / storageStats.total) * 100, 100) + "%" }}
                            ></div>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <div className="text-gray-600">여유 공간</div>
                              <div className="font-bold text-green-600">
                                {(storageStats.total - parseFloat(storageStats.used)).toFixed(1)}MB
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-600"></div>
                              <div className="font-bold">
                                {Math.round((parseFloat(storageStats.used) / storageStats.total) * 100)}%
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* 업로드 활동 요약 */}
                    <Card>
                      <CardHeader>
                        <CardTitle>📚 파일 활동 요약</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                          <div>
                            <div className="text-2xl font-bold text-blue-900">{uploadedFiles.length}</div>
                            <div className="text-sm text-blue-700">�?파일</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-green-900">
                              {uploadedFiles.reduce((sum, file) => sum + (file.downloads || 0), 0)}
                            </div>
                            <div className="text-sm text-green-700">�?다운로드</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-purple-900">
                              {uploadedFiles.length > 0 ? 
                                Math.round(uploadedFiles.reduce((sum, file) => sum + (file.downloads || 0), 0) / uploadedFiles.length * 10) / 10 
                                : 0}
                            </div>
                            <div className="text-sm text-purple-700">평균 다운로드</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-orange-900">
                              {storageStats.files > 0 ? (parseFloat(storageStats.used) / storageStats.files).toFixed(1) : 0}MB
                            </div>
                            <div className="text-sm text-orange-700">평균 파일 크기</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* 설정 ??*/}
                <TabsContent value="settings" className="space-y-6">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">⚠️ 파일 업로드??설정</h2>
                    
                    <div className="space-y-6">
                      {/* 기본�?설정 */}
                      <Card>
                        <CardHeader>
                          <CardTitle>📚 기본�?설정</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                기본 과목
                              </label>
                              <select
                                value={uploadSettings.defaultSubject}
                                onChange={(e) => setUploadSettings(prev => ({
                                  ...prev,
                                  defaultSubject: e.target.value
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                {englishSubjects.map(subject => (
                                  <option key={subject} value={subject}>{subject}</option>
                                ))}
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                기본 사이즈                              </label>
                              <select
                                value={uploadSettings.defaultDifficulty}
                                onChange={(e) => setUploadSettings(prev => ({
                                  ...prev,
                                  defaultDifficulty: e.target.value
                                }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                {difficulties.map(diff => (
                                  <option key={diff} value={diff}>{diff}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* 업로드 옵션 */}
                      <Card>
                        <CardHeader>
                          <CardTitle>📚 업로드 옵션</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium text-gray-900">자동 새로고침</div>
                                <div className="text-sm text-gray-600">파일 업로드??✓ 완료??자동 새로고침</div>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={uploadSettings.autoRefresh}
                                  onChange={(e) => setUploadSettings(prev => ({
                                    ...prev,
                                    autoRefresh: e.target.checked
                                  }))}
                                  className="sr-only peer"
                                />
                                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                              </label>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                최대 파일 크기 (MB)
                              </label>
                              <div className="flex items-center space-x-4">
                                <input
                                  type="range"
                                  min="1"
                                  max="5"
                                  step="0.5"
                                  value={uploadSettings.maxFileSize}
                                  onChange={(e) => setUploadSettings(prev => ({
                                    ...prev,
                                    maxFileSize: parseFloat(e.target.value)
                                  }))}
                                  className="flex-1"
                                />
                                <span className="font-medium text-gray-900 w-12 text-right">
                                  {uploadSettings.maxFileSize}MB
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* 스토리지 관리*/}
                      <Card>
                        <CardHeader>
                          <CardTitle>⚙️ 저장소 관리</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            <Alert>
                              <span className="text-lg">📚</span>
                              <AlertTitle>스토리지 정보</AlertTitle>
                              <AlertDescription>
                                현재 {storageStats.used}MB / {storageStats.total}MB 사용 중입니다.
                                📚? 공간: {(storageStats.total - parseFloat(storageStats.used)).toFixed(1)}MB
                              </AlertDescription>
                            </Alert>

                            {parseFloat(storageStats.used) > 4 && (
                              <Alert variant="destructive">
                                <AlertTitle>저장 공간 부족</AlertTitle>
                                <AlertDescription>
                                  여유 공간이 부족합니다. 불필요한 파일을 삭제하거나 파일 크기를 줄여주세요.
                                </AlertDescription>
                              </Alert>
                            )}

                            <div className="flex gap-2">
                              <Button
                                onClick={() => {
                                  loadUploadedFiles();
                                  updateStorageStats();
                                  setMessage('??스토리지 정보가 업데이트되었습니다!');
                                  setTimeout(() => setMessage(''), 3000);
                                }}
                                variant="outline"
                              >
                                📚 스토리지 새로고침
                              </Button>

                              {uploadedFiles.length > 0 && (
                                <Button
                                  onClick={clearAllFiles}
                                  variant="destructive"
                                >
                                  📚�?모든 파일 ?📚
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>
              </CardContent>
            </Tabs>
          </Card>
        </div>
      </div>
    );
  };
  
  const renderPage = () => {
    switch(currentPage) {
      case 'home': return <NewHomePage user={user} navigate={setCurrentPage} dark={isDarkMode} toggleTheme={() => setThemeMode(isDarkMode ? 'light' : 'dark')} openLibrary={openDesignLibrary} openLearning={openDesignLearning} />;
      case 'learning_demo': return <LearningDemo onExit={() => setCurrentPage('home')} onStart={() => setCurrentPage(user ? 'online_test' : 'signup')} />;
      case 'login': return <LoginPage />;
      case 'signup': return <SignupPage />;
      case 'reset-password': return <ForgotPasswordPage />;
      case 'email-verification-required': return <EmailVerificationRequiredPage />;
      case 'account-locked': return <AccountLockedPage />;

      // 모의고사 변형문제 (유료 구독 필요)
      case 'mock_variation':
        // 로그인 체크
        if (!user) {
          return <LearningAccess user={user} variation navigate={navigateDesignPage} />;
        }
        // 구독 체크
        if (!canAccessMockVariation()) {
          return <LearningAccess user={user} variation navigate={navigateDesignPage} />;
        }
        return (
          <MockVariationSelectionPage
            showToast={showToast}
            onSelect={(grade, exam, questionNum) => {
              setFreeMockGrade(grade);
              setFreeMockExam(exam);
              setFreeMockQuestionNum(questionNum);
              setCurrentPage('mock_variation_test');
            }}
            onBack={() => setCurrentPage('home')}
          />
        );

      case 'mock_variation_test':
        // 구독 체크
        if (!canAccessMockVariation()) {
          setCurrentPage('mock_variation');
          return null;
        }
        return (
          <MockVariationTestPage
            grade={freeMockGrade}
            exam={freeMockExam}
            questionNum={freeMockQuestionNum}
            user={user}
            showToast={showToast}
            onExit={() => setCurrentPage('mock_variation')}
          />
        );

      // 기존 무료 모의고사 (하위호환 - 유료로 리다이렉트)
      case 'mock_variation_free':
        setCurrentPage('mock_variation');
        return null;

      case 'mock_variation_test_free':
        setCurrentPage('mock_variation');
        return null;

      case 'online_test':
        // 온라인 학습 권한 체크
        if (!canAccessOnlineLearning()) {
          return <LearningAccess user={user} navigate={navigateDesignPage} />;
        }
        return (
          <OnlineTestPage key={onlineNavigationKey}
            user={user}
            showToast={showToast}
            initialDay={resumeVocabularyDay}
            initialStyle={resumeVocabularyStyle}
            initialChapter={resumeGrammarChapter}
            initialBluebookStage={bluebookStage}
            initialBluebookRound={bluebookRound}
            initialBluebookMode={bluebookMode}
            bluebookSavedSession={bluebookSavedSession}
            initialExamGrade={resumeExamGrade}
            initialExamExam={resumeExamExam}
            initialExamQuestionNum={resumeExamQuestionNum}
            reviewMode={vocabularyReviewMode}
            returnToReview={returnToReview}
          />
        );
      case 'bluebook':
        return (
          <BluebookPage
            user={user}
            canDownload={canDownload}
            downloadPDF={downloadPDF}
            getStatusMessage={getStatusMessage}
            hasSubscription={hasSubscription}
            setCurrentPage={setCurrentPage}
            showToast={showToast}
          />
        );
      case 'download':
      case 'library':
        return (
          <EnglishLibraryPage
            user={user}
            canDownload={canDownload}
            canDownloadCategory={canDownloadCategory}
            downloadPDF={downloadPDF}
            getStatusMessage={getStatusMessage}
            hasSubscription={hasSubscription}
            setCurrentPage={setCurrentPage}
            initialCategory={libraryCategory}
            showToast={showToast}
            planType={subscription?.planType}
          />
        );
      case 'quiz':
        return <OnlineQuiz showToast={showToast} />;
      case 'customer_service': return <CustomerServicePage setCurrentPage={setCurrentPage} />;
      case 'partnership': return <PartnershipInquiryPage setCurrentPage={setCurrentPage} />;
      case 'inquiry': return <InquiryPage setCurrentPage={setCurrentPage} user={user} />;
      case 'pricing': return <PricingPage />;
      case 'faq': return <FAQPage />;
      case 'notice': return <NoticePage />;
      case 'terms': return <TermsOfServicePage />;
      case 'privacy': return <PrivacyPolicyPage />;
      case 'refund': return <RefundPolicyPage onBack={() => setCurrentPage('home')} />;
      case 'ecommerce': return <EcommercePolicyPage onBack={() => setCurrentPage('home')} />;
      case 'profile':
        // profile은 dashboard로 리다이렉트
        return (
          <UserDashboard
            user={user}
            subscription={subscription}
            hasSubscription={hasSubscription}
            isActive={isActive}
            isExpired={isExpired}
            downloadsRemaining={downloadsRemaining}
            totalDownloads={totalDownloads}
            getDaysRemaining={getDaysRemaining}
            getUsagePercentage={getUsagePercentage}
            planName={planName}
            referralStats={referralStats}
            myReferralCode={myReferralCode}
            navigateToLibrary={navigateToLibrary}
            copyShareText={copyShareText}
            copyShareLink={copyShareLink}
            generateReferralLink={generateReferralLink}
            resumeVocabularyTest={resumeVocabularyTest}
            leaderboard={referralLeaderboard}
            isUnlimited={isUnlimited}
          />
        );
      case 'dashboard':
        return (
          <UserDashboard
            user={user}
            subscription={subscription}
            hasSubscription={hasSubscription}
            isActive={isActive}
            isExpired={isExpired}
            downloadsRemaining={downloadsRemaining}
            totalDownloads={totalDownloads}
            getDaysRemaining={getDaysRemaining}
            getUsagePercentage={getUsagePercentage}
            planName={planName}
            referralStats={referralStats}
            myReferralCode={myReferralCode}
            navigateToLibrary={navigateToLibrary}
            copyShareText={copyShareText}
            copyShareLink={copyShareLink}
            generateReferralLink={generateReferralLink}
            setCurrentPage={setCurrentPage}
            resumeVocabularyTest={resumeVocabularyTest}
            resumeGrammarTest={resumeGrammarTest}
            leaderboard={referralLeaderboard}
            isUnlimited={isUnlimited}
          />
        );
      case 'analytics':
        return (
          <AnalyticsPage
            user={user}
            hasSubscription={hasSubscription}
            setCurrentPage={setCurrentPage}
          />
        );
      case 'review':
        return (
          <ReviewPage
            key={reviewPageKey}  // key를 사용하여 컴포넌트 언마운트
            user={user}
            setCurrentPage={setCurrentPage}
            resumeVocabularyTest={resumeVocabularyTest}
            resumeGrammarTest={resumeGrammarTest}
            resumeBluebookTest={resumeBluebookTest}
            resumeExamTest={resumeExamTest}
            showToast={showToast}
          />
        );
      case 'admin-setup': return <AdminSetup user={user} />;
      case 'admin': return <AdminPage user={user} />;
      case 'upload': return <UploadPage />;
      case 'firebase-test': return <FirebaseTestComponent user={user} />;
      
      case 'payment':
        return (
          <PaymentPage
            selectedPlan={selectedPlan}
            user={user}
            appliedPromo={appliedPromo}
            getDiscountedPrice={getDiscountedPrice}
            onPaymentSuccess={async (paymentData) => {
              console.log('✅ 결제 성공:', paymentData);

              // 📚 서버 구독 정보 새로고침
              if (refreshSubscription) {
                await refreshSubscription();
                console.log('✅ 구독 정보 업데이트 완료');
              }

              setCurrentPage('payment-success');
            }}
            onCancel={() => {
              setSelectedPlan(null);
              setCurrentPage('pricing');
            }}
            trialStatus={trialStatus}
          />
        );

      case 'payment-success':
        return (
          <PaymentSuccessPage
            user={user}
          />
        );

      case 'payment-fail':
        return (
          <PaymentFailPage />
        );
      
      default: return <NewHomePage user={user} navigate={setCurrentPage} dark={isDarkMode} toggleTheme={() => setThemeMode(isDarkMode ? 'light' : 'dark')} openLibrary={openDesignLibrary} openLearning={openDesignLearning} />;
    }
  };

  // 공개 페이지는 로그인 없이 바로 표시
  const publicPages = ['home', 'login', 'signup', 'reset-password', 'faq', 'notice', 'terms', 'privacy', 'pricing'];
  const isPublicPage = publicPages.includes(currentPage);

  // 로딩 화면 표시 조건: 로딩 중 && 공개 페이지 아님 && 사용자가 로그인됨
  // (로그인된 사용자는 로딩 완료까지 기다려야 페이지를 볼 수 없음)
  // 또는 카카오 로그인 콜백 처리 중일 때도 로딩 화면 표시
  if ((loading && !isPublicPage && !user) || kakaoCallbackProcessingRef.current) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">{loadingMessage || '로딩 중...'}</p>
          {loadingMessage && (
            <p className="mt-2 text-sm text-gray-400">잠시만 기다려주세요</p>
          )}
        </div>
      </div>
    );
  }

  // 만료 알림 컴포넌트
  const ExpiryPopup = () => {
    if (!showExpiryPopup) return null;

    const daysRemaining = subscription?.daysRemaining || 0;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md mx-4">
          <h3 className="text-xl font-bold mb-4">무료 체험 만료 알림</h3>
          <p className="mb-4">
            {user?.displayName || '고객'}님의 무료 체험이<strong className="text-red-600">{daysRemaining}</strong> 일 후 만료됩니다.
          </p>
          <p className="mb-6 text-sm text-gray-600">
            만료 전에 남아있는 다운로드 횟수가 소멸됩니다! 구독 플랜으로 전환하여 계속 학습하세요!
          </p>
          <div className="flex space-x-3">
            <button
              onClick={() => {
                setShowExpiryPopup(false);
                setCurrentPage('pricing');
              }}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              구독 플랜 보기
            </button>
            <button
              onClick={() => setShowExpiryPopup(false)}
              className="flex-1 bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              인기
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 무료 체험 만료 모달
  const TrialExpiredModal = () => {
    if (!showTrialExpiredModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-lg mx-4 shadow-2xl">
          <div className="text-center mb-6">
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4">
              <Clock className="h-10 w-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">무료 체험이 종료되었습니다</h3>
            <p className="text-gray-600">
              24시간 무료 체험을 통해 온라인 테스트를 경험해 보셨나요?
            </p>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 mb-6">
            <h4 className="font-semibold text-gray-800 mb-3">🎁 구독하면 이런 혜택이!</h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-center">
                <span className="text-green-500 mr-2"></span>
                온라인 테스트 무제한 사용
              </li>
              <li className="flex items-center">
                <span className="text-green-500 mr-2"></span>
                PDF 자료 다운로드 (프리미엄 500회 / 마스터 1,000회)
              </li>
              <li className="flex items-center">
                <span className="text-green-500 mr-2"></span>
                TTS 발음 듣기 및 정답노트 기능
              </li>
              <li className="flex items-center">
                <span className="text-green-500 mr-2"></span>
                학습 분석 대시보드              </li>
            </ul>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
            <p className="text-sm text-amber-800">
              📚 <strong>Tip:</strong> 가입 보너스 10회 다운로드는 30일간 유효합니다. 온라인 테스트는 구독 시 무제한!
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => {
                setShowTrialExpiredModal(false);
                setCurrentPage('pricing');
              }}
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 font-semibold shadow-lg transition-all"
            >
              🚀 구독 플랜 보기
            </button>
            <button
              onClick={() => setShowTrialExpiredModal(false)}
              className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-200 font-medium transition-all"
            >
              나중에 하기
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ExpiryBanner = () => {
    if (!showExpiryBanner) return null;

    const daysRemaining = subscription?.daysRemaining || 0;
    const bgColor = daysRemaining <= 3 ? 'bg-red-100 border-red-500' : 'bg-yellow-100 border-yellow-500';
    const textColor = daysRemaining <= 3 ? 'text-red-800' : 'text-yellow-800';

    return (
      <div className={bgColor + " border-l-4 p-4 mb-4"}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Clock className={"h-5 w-5 " + textColor + " mr-3"} />
            <p className={textColor + " font-medium"}>
              무료 체험이 <strong>{daysRemaining}일</strong> 후 만료됩니다. 남은 다운로드를 사용하거나 구독 플랜으로 전환하세요.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage('pricing')}
              className={(daysRemaining <= 3 ? "bg-red-600" : "bg-yellow-600") + " text-white px-4 py-2 rounded-lg hover:opacity-90 text-sm font-medium"}
            >
              구독하기
            </button>
            <button
              onClick={() => setShowExpiryBanner(false)}
              className={textColor + " hover:opacity-70"}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const ExpiryModal = () => {
    if (!showExpiryModal) return null;

    const daysRemaining = subscription?.daysRemaining || 0;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-lg mx-4 shadow-2xl">
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Clock className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="text-2xl font-bold mb-2">🚨 긴급 알림</h3>
            <p className="text-lg text-red-600 font-semibold">
              무료 체험이 {daysRemaining}일 후 만료됩니다.
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-gray-700 mb-2">
              {user?.displayName || '고객'}님, 남은 이용 혜택은 체험 만료 시 종료됩니다.
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li>남은 다운로드 횟수: <strong>{subscription?.downloadsRemaining || 0}회</strong></li>
              <li>섹션별 무료 다운로드 권한</li>
              <li>모든 학습 자료 접근 권한</li>
            </ul>
          </div>
          <p className="text-center text-gray-600 mb-6">
            지금 구독하시면 계속해서 학습을 이어갈 수 있습니다!
          </p>
          <div className="flex space-x-3">
            <button
              onClick={() => {
                setShowExpiryModal(false);
                setCurrentPage('pricing');
              }}
              className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-semibold"
            >
              구독 플랜 보기
            </button>
            <button
              onClick={() => setShowExpiryModal(false)}
              className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300"
            >
              나중에            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Routes>
      {/* 결제 성공 페이지 */}
      <Route path="/payment/success" element={<div className="bb-site bb-surface"><Suspense fallback={<LoadingSpinner />}><PaymentSuccessPage user={user} /></Suspense></div>} />

      {/* 결제 실패 페이지 */}
      <Route path="/payment/fail" element={<div className="bb-site bb-surface"><Suspense fallback={<LoadingSpinner />}><PaymentFailPage /></Suspense></div>} />

      {/* 이메일 인증 페이지 (커스텀 SMTP) */}
      <Route path="/verify-email" element={
        <div className="bb-site bb-surface"><Suspense fallback={<LoadingSpinner />}>
          <VerifyEmailPage />
        </Suspense></div>
      } />

      {/* 비밀번호 재설정 페이지 (커스텀 SMTP) */}
      <Route path="/reset-password" element={
        <div className="bb-site bb-surface"><Suspense fallback={<LoadingSpinner />}>
          <ResetPasswordPage />
        </Suspense></div>
      } />

      {/* 메인 앱 */}
      <Route path="*" element={
        <div className={`bb-site min-h-screen flex flex-col ${user ? 'bb-has-bottom-nav' : ''}`}>
          {currentPage !== 'home' && <SiteHeader user={user} page={currentPage} navigate={navigateDesignPage} openLibrary={openDesignLibrary} openLearning={openDesignLearning} dark={isDarkMode} themeMode={themeMode} setThemeMode={setThemeMode} planName={getDisplayPlanName()} daysRemaining={getDaysRemaining()} isAdmin={isLocalAdmin || userRole === 'admin'} onLogout={handleLogout} onInstall={showInstallButton ? handleInstallClick : null} />}
          <ExpiryBanner />

          {/* 웹 (xl 이상) - 사이드바 + 메인 레이아웃 */}
          <div className={currentPage === 'home' ? 'flex flex-1' : 'bb-app-layout'}>
            {/* 왼쪽 사이드바 - 라이트모드: 밝은 배경, 다크모드: 어두운 배경 */}
            {['library','bluebook','online_test','review','dashboard','analytics','mock_variation','mock_variation_test'].includes(currentPage) && <SiteSidebar page={currentPage} navigate={navigateDesignPage} openLibrary={openDesignLibrary} openLearning={openDesignLearning} />}

            {/* 메인 콘텐츠 */}
            <main key={user?.uid || 'guest'} className={`bb-app-main bb-learning-ui ${['home','learning_demo','admin','admin-setup','upload','firebase-test'].includes(currentPage) ? '' : 'bb-surface'}`}>
              <Suspense fallback={<LoadingSpinner />}>
                {renderPage()}
              </Suspense>
            </main>
          </div>

          <Footer setCurrentPage={page => page === 'library' ? openDesignLibrary() : page === 'online_test' ? openDesignLearning() : navigateDesignPage(page)} />

          {/* 모바일 하단 네비게이션 바*/}
          {user && (
            <BottomNavigation
              currentPage={currentPage}
              setCurrentPage={page => page === 'library' ? openDesignLibrary() : page === 'online_test' ? openDesignLearning() : navigateDesignPage(page)}
              user={user}
            />
          )}

          {/* 학습 시작하기 모달 */}
          {showStudyModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowStudyModal(false)}>
              <div
                className="bg-white dark:bg-slate-800 rounded-2xl p-6 sm:p-8 max-w-md w-full mx-4 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-center mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    학습 방법 선택
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    원하시는 학습 방법을 선택해주세요
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* 온라인 학습 */}
                  <button
                    onClick={() => {
                      const tab = selectedCardType || 'exam';
                      const searchParams = new URLSearchParams(window.location.search);
                      searchParams.set('tab', tab);
                      const newUrl = window.location.pathname + '?' + searchParams.toString() + '#online_test';
                      window.history.replaceState(null, '', newUrl);
                      setCurrentPage('online_test');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                      setShowStudyModal(false);
                      setSelectedCardType(null);
                    }}
                    className="flex flex-col items-center gap-3 p-6 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-xl border-2 border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-lg transition-all group"
                  >
                    <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <span className="text-2xl">💻</span>
                    </div>
                    <span className="font-bold text-gray-900 dark:text-white">온라인 학습</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 text-center">
                      문제 풀이 & 자동 채점
                    </span>
                  </button>

                  {/* 자료 다운로드 */}
                  <button
                    onClick={() => {
                      const tab = selectedCardType || 'basics';
                      window.location.search = `?tab=${tab}`;
                      setCurrentPage('bluebook');
                      setShowStudyModal(false);
                      setSelectedCardType(null);
                    }}
                    className="flex flex-col items-center gap-3 p-6 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 rounded-xl border-2 border-green-200 dark:border-green-700 hover:border-green-400 dark:hover:border-green-500 hover:shadow-lg transition-all group"
                  >
                    <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <span className="text-2xl">📥</span>
                    </div>
                    <span className="font-bold text-gray-900 dark:text-white">자료 다운로드</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 text-center">
                      PDF 자료 & 오프라인 학습
                    </span>
                  </button>
                </div>

                <button
                  onClick={() => { setShowStudyModal(false); setSelectedCardType(null); }}
                  className="mt-6 w-full py-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-sm font-medium transition-colors"
                >
                  닫기
                </button>
              </div>
            </div>
          )}

          <ToastContainer />
          <ExpiryPopup />
          <ExpiryModal />
          <TrialExpiredModal />
          <MockVariationAnnouncementModal
            isOpen={showMockVariationModal}
            onClose={() => setShowMockVariationModal(false)}
            onDontShowToday={handleMockVariationDontShowToday}
            onNavigate={setCurrentPage}
          />
        </div>
      } />
    </Routes>
  );
};

export default App;
